<template>
  <!-- Cart + Top 5 are now provided globally as floating panels
       (mounted once in App.vue). The dashboard no longer hosts its
       own inline cart/Top 5, so the cart-driven layout classes that
       used to swap columns here are gone. -->
  <section id="account">
    <div id="account-banner" style="display:none;">
      <!-- Old banner removed - hidden via CSS -->
    </div>

    <!-- ===== ACCOUNT HEADER + IMPORTANT INFO BLOCK =====
         Three stacked sections in a single visual flow:
           1. "Welcome back, Michael!" — top
           2. Important Information — middle, single shadowed
              container with three bullet rows
           3. "Here are some insights just for you!" — bottom

         The user's explicit ordering: welcome above, the three
         system messages in the middle as full-width bullet rows
         inside one card, and the insights tagline below the
         messages so it directly precedes the dashboard content
         it introduces. No per-message card chrome — a single
         shadowed container holds all three. -->
    <!-- The "Welcome back" heading now lives at the TOP of #acctInsights
         below (directly above the insights tagline + dashboard content),
         so it sits right above the content with no cross-section gap. The
         former #acctImportantInfo section is gone: its welcome heading
         moved into #acctInsights, and the inline Important Information
         panel it used to host is now the global floating
         ImportantInfoWidget (pill + modal). -->

    <!-- Old toggle-driven tiles container — kept for the
         admin path below where #adminWelcome lives, but the
         block itself stays empty since the v-show guard is
         gone and the content has been promoted to the new
         #acctImportantInfo section above. The empty wrapper
         is preserved (rather than deleted) so any external
         CSS targeting #account-tiles by ID for spacing /
         page-level reset doesn't break. -->
    <div id="account-tiles">
    </div>

    <!-- Admin welcome: shown to master-role users when no company is selected -->
    <div id="adminWelcome" v-if="!companySelected && isMasterUser">
      <div class="container">
        <select-company-prompt :user-name="currentUser.firstName || 'Admin'" />
      </div>
    </div>

    <div id="acctInsights" class="acct-insights-tight" v-if="companySelected">
      <div class="container">
        <!-- Welcome heading — lives at the top of #acctInsights so it
             sits directly above the insights tagline + dashboard content
             with no gap (moved here from the former #acctImportantInfo
             section, which used to sit a full section above). -->
        <h1 class="dashboard-welcome dashboard-welcome-top">
          Welcome back, {{ currentUser.firstName || currentUser.email }}!
        </h1>
        <!-- "Here are some insights just for you!" tagline.
             Sits at the top of #acctInsights, immediately
             above the upper-grid row that hosts the four
             insight stat cards. Previously placed at the
             bottom of dashboard-welcome (next to the
             Important Information block), but the visual
             distance from the cards made it read as more of
             a closing line for the messages section than as
             an introduction to the insights — moved here so
             it sits right above the data it's introducing. -->
        <p class="dashboard-welcome-subtext-line acct-insights-tagline">
          Here are some insights just for you!
        </p>

        <!-- ===== UPPER GRID =====
             8/4 split: the four insight stat cards span only the
             left two-thirds of the container so their bottom edge
             aligns visually with the top of Completed Orders and
             Completed Quotes below them. The right third is the
             Add-to-Cart staging tray, which mirrors the right
             panel on Search.vue — selectedParts (orders) and
             selectedPartsQuote (quotes) grouped by Purchase Order
             # with a × per row. Sits directly above Top 5 Most
             Purchased Parts in the bottom row, giving the user
             a one-glance view of what's queued, what they've
             frequently ordered, and a basket icon to add more.
             Below lg the columns stack to a single column.

             When the user has parked Top 5 in its side-edge pill
             (TopPartsWidget emits inline-visibility-change(false)
             → topPartsInlineVisible = false), right-column-flex
             doesn't render and the left col expands to col-12 so
             the insights stats + completed cards fill the full
             row rather than leaving a permanent empty third on
             the right. -->
        <div class="row mt-3 mb-4 dashboard-upper-grid">
          <!-- The insights stats + completed cards take col-lg-8
               when Top 5 is rendered to their right in this row.
               They expand to col-12 when (a) the user has parked
               Top 5 in the side-rail pill (signalled via
               topPartsInlineVisible from the widget), OR (b) the
               cart is inline above and Top 5 has been relocated
               into the cart column there — in either case right-
               column-flex below doesn't render, so this column
               should claim the freed third. -->
          <div class="col-12">
            <!-- ===== INSIGHTS CONTAINER =====
                 All four insight stats (Total Spend banner +
                 Orders / Quotes / Saved Carts) live in a single
                 panel rather than each carrying its own card
                 chrome. The user's call: a single bordered
                 container reads as one cohesive "insights"
                 unit instead of four loose tiles.

                 The .insight-card class on each child still
                 exists in the markup for class-based selectors
                 elsewhere, but its visual chrome (border /
                 shadow / bg) has been stripped in CSS so only
                 the OUTER .insights-container shows the
                 white-bg + red-top + shadow signature shared
                 with the staging tray and Top 5 panels. -->
            <div class="insights-container">
              <div class="row insights-card-row insights-card-row-compact">
              <!-- ===== TOTAL SPEND BANNER =====
                   Sits above the other three stat cards as a
                   full-width banner so the dollar figure has
                   visual weight commensurate with its importance.
                   The other three cards (Orders / Quotes / Saved
                   Carts) sit underneath as a row of three.

                   Hosts the YTD / All Time toggle on the right —
                   the toggle previously lived inside the Top 5
                   panel but switches `insightsPeriod` which
                   already drives every YTD-labeled metric on
                   the page (Total Spend, Orders, Quotes, AND
                   Top 5's data). Putting the toggle here pins
                   it to the section that visually changes when
                   it's toggled, rather than burying it inside
                   the Top 5 panel where the user might not
                   realize it controls the upper cards too. -->
              <div class="col-12 mb-3">
                <div class="insight-card insight-card-compact insight-card-banner">
                  <div class="insight-banner-inner">
                    <div class="insight-banner-label">
                      Total Spend ({{ insightsPeriodLabel }})
                    </div>
                    <div class="insight-banner-number">
                      {{ insights.ytdTotalSpend | currency }}
                    </div>
                    <div class="insights-toggle-banner">
                      <div class="insights-toggle">
                        <button
                          type="button"
                          class="btn btn-sm"
                          :class="insightsPeriod === 'ytd' ? 'btn-dark' : 'btn-outline-dark'"
                          @click="switchInsightsPeriod('ytd')"
                        >
                          YTD
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm"
                          :class="insightsPeriod === 'alltime' ? 'btn-dark' : 'btn-outline-dark'"
                          @click="switchInsightsPeriod('alltime')"
                        >
                          All Time
                        </button>
                      </div>
                      <!-- Account scope — sits right of YTD/All Time. Governs
                           every metric + list below (insights, Completed
                           Orders, Completed Quotes, Top 5). "All account
                           orders" = everyone on the company; "Just mine" =
                           only this user's. -->
                      <div class="insights-toggle insights-toggle-scope"
                           :title="accountScope === 'account' ? 'Showing everyone on this account' : 'Showing only orders you placed'">
                        <button
                          type="button"
                          class="btn btn-sm"
                          :class="accountScope === 'account' ? 'btn-dark' : 'btn-outline-dark'"
                          @click="switchAccountScope('account')"
                        >
                          All account orders
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm"
                          :class="accountScope === 'mine' ? 'btn-dark' : 'btn-outline-dark'"
                          @click="switchAccountScope('mine')"
                        >
                          Just mine
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-4">
                <div class="insight-card insight-card-compact">
                  <div class="insight-number">
                    <router-link
                      v-if="orderActivityTotal > 0"
                      to="/account/orderhistory"
                      class="insight-link"
                    >
                      {{ orderActivityTotal }}
                    </router-link>
                    <span v-else>{{ orderActivityTotal }}</span>
                  </div>
                  <div class="insight-label">Orders ({{ insightsPeriodLabel }})</div>
                  <div class="insight-subtext">
                    <router-link
                      v-if="processedOrderCount > 0"
                      to="/account/orderhistory"
                      class="insight-mini-link"
                    >
                      {{ processedOrderCount }}
                    </router-link>
                    <span v-else class="insight-mini-value">{{ processedOrderCount }}</span>
                    <span class="insight-subtext-label">Completed</span>
                    <span class="insight-subtext-sep">/</span>
                    <router-link
                      v-if="savedOrderCount > 0"
                      to="/account/savedcarts"
                      class="insight-mini-link"
                    >
                      {{ savedOrderCount }}
                    </router-link>
                    <span v-else class="insight-mini-value">{{ savedOrderCount }}</span>
                    <span class="insight-subtext-label">Unfinished</span>
                  </div>
                </div>
              </div>

              <div class="col-4">
                <div class="insight-card insight-card-compact">
                  <div class="insight-number">
                    <router-link
                      v-if="quoteActivityTotal > 0"
                      to="/account/rfqhistory"
                      class="insight-link"
                    >
                      {{ quoteActivityTotal }}
                    </router-link>
                    <span v-else>{{ quoteActivityTotal }}</span>
                  </div>
                  <div class="insight-label">Quotes ({{ insightsPeriodLabel }})</div>
                  <div class="insight-subtext">
                    <router-link
                      v-if="processedQuoteCount > 0"
                      to="/account/rfqhistory"
                      class="insight-mini-link"
                    >
                      {{ processedQuoteCount }}
                    </router-link>
                    <span v-else class="insight-mini-value">{{ processedQuoteCount }}</span>
                    <span class="insight-subtext-label">Completed</span>
                    <span class="insight-subtext-sep">/</span>
                    <router-link
                      v-if="savedQuoteCount > 0"
                      to="/account/savedcarts"
                      class="insight-mini-link"
                    >
                      {{ savedQuoteCount }}
                    </router-link>
                    <span v-else class="insight-mini-value">{{ savedQuoteCount }}</span>
                    <span class="insight-subtext-label">Unfinished</span>
                  </div>
                </div>
              </div>

              <div class="col-4">
                <div class="insight-card insight-card-compact">
                  <div class="insight-number">
                    <router-link
                      v-if="savedCartTotal > 0"
                      to="/account/savedcarts"
                      class="insight-link"
                    >
                      {{ savedCartTotal }}
                    </router-link>
                    <span v-else>{{ savedCartTotal }}</span>
                  </div>
                  <div class="insight-label">Saved Carts</div>
                  <div class="insight-subtext">
                    <router-link
                      v-if="savedOrderCount > 0"
                      to="/account/savedcarts"
                      class="insight-mini-link"
                    >
                      {{ savedOrderCount }}
                    </router-link>
                    <span v-else class="insight-mini-value">{{ savedOrderCount }}</span>
                    <span class="insight-subtext-label">Orders</span>
                    <span class="insight-subtext-sep">/</span>
                    <router-link
                      v-if="savedQuoteCount > 0"
                      to="/account/savedcarts"
                      class="insight-mini-link"
                    >
                      {{ savedQuoteCount }}
                    </router-link>
                    <span v-else class="insight-mini-value">{{ savedQuoteCount }}</span>
                    <span class="insight-subtext-label">Quotes</span>
                  </div>
                </div>
              </div>
            </div>
            </div>

            <!-- ===== COMPLETED ORDERS + COMPLETED QUOTES =====
                 Sit directly under the stat-card row inside the
                 left col-lg-8 column so they don't get pushed
                 down by a tall staging tray on the right. Each
                 card is col-md-6 within col-lg-8, giving them
                 roughly the same on-screen width as before
                 (~33% of viewport). At lg-down, they wrap
                 single-column. -->
            <div class="row dashboard-completed-row">
              <div class="col-md-6 mb-4 mb-md-0">
                <div class="dashboard-card">
                  <div class="dashboard-card-head">
                    <h3 class="dashboard-card-title">Completed Orders</h3>
                  </div>

                  <div v-if="!companySelected" class="dashboard-empty">
                    Select a company to view your recent orders.
                  </div>

                  <ul v-else-if="orders && orders.length > 0"
                      class="dashboard-list">
                    <li class="dashboard-list-item"
                        v-for="o in orders.slice(0, 5)"
                        :key="'ord-' + o.id">
                      <!-- Subtle per-order company toggle (Feature 4). The
                           chevron sits at the far left of the row; clicking
                           it reveals the order's "Company Name – ID" line
                           below. Kept compact + low-emphasis so it doesn't
                           compete with the date / amount / status. -->
                      <button class="dashboard-list-expand"
                              type="button"
                              :class="{ 'is-open': !!expandedOrderCompany[o.id] }"
                              @click="toggleOrderCompany(o.id)"
                              :aria-expanded="expandedOrderCompany[o.id] ? 'true' : 'false'"
                              :aria-label="(expandedOrderCompany[o.id] ? 'Hide' : 'Show') + ' company for this order'"
                              title="Show company">
                        <span class="dashboard-list-expand-icon" aria-hidden="true">+</span>
                      </button>
                      <div class="dashboard-list-main">
                        <div class="dashboard-list-row1">
                          <span class="dashboard-list-date">
                            {{ o.dateCreated | formatDate }}
                          </span>
                          <span class="dashboard-list-amount">
                            {{ o.cost | currency }}
                          </span>
                        </div>
                        <div class="dashboard-list-meta">
                          <span class="dashboard-list-status"
                                :class="(o.status || '').toLowerCase().indexOf('complete') >= 0
                                  ? 'is-complete' : 'is-open'">
                            {{ o.status || '—' }}
                          </span>
                        </div>
                        <!-- Expanded detail: the order's company. Shown only
                             when this order's chevron is toggled open. -->
                        <div v-if="expandedOrderCompany[o.id]"
                             class="dashboard-list-company">
                          <span class="dashboard-list-company-label">Company:</span>
                          <span class="dashboard-list-company-value">{{ orderCompanyDisplay(o) }}</span>
                        </div>
                      </div>
                      <button class="dashboard-action-btn dashboard-action-icon"
                              @click="onReorderClick(o)"
                              :title="'Re-order for ' + orderCompanyDisplay(o)"
                              :aria-label="'Re-order for ' + orderCompanyDisplay(o)">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"
                             width="17" height="17" aria-hidden="true">
                          <path fill="currentColor" d="M463.5 224l8.5 0c13.3 0 24-10.7 24-24l0-128c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1c-87.5 87.5-87.5 229.3 0 316.8s229.3 87.5 316.8 0c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0c-62.5 62.5-163.8 62.5-226.3 0s-62.5-163.8 0-226.3c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8l119.5 0z"/>
                        </svg>
                      </button>
                    </li>
                  </ul>

                  <div v-else class="dashboard-empty">
                    No recent orders.
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="dashboard-card">
                  <div class="dashboard-card-head">
                    <h3 class="dashboard-card-title">Completed Quotes</h3>
                  </div>

                  <div v-if="!companySelected" class="dashboard-empty">
                    Select a company to view your recent quotes.
                  </div>

                  <ul v-else-if="quotes && quotes.data && quotes.data.length > 0"
                      class="dashboard-list">
                    <li class="dashboard-list-item"
                        v-for="q in (quotes.data || []).slice(0, 5)"
                        :key="'quo-' + q.id">
                      <div class="dashboard-list-main">
                        <div class="dashboard-list-row1">
                          <span class="dashboard-list-date">
                            {{ q.dateCreated | formatDate }}
                          </span>
                          <span class="dashboard-list-amount">
                            {{ q.cost | currency }}
                          </span>
                        </div>
                      </div>
                      <button class="dashboard-action-btn dashboard-action-icon"
                              @click="viewQuoteDetails(q.id)"
                              title="Review quote details">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"
                             width="17" height="17" aria-hidden="true">
                          <path fill="currentColor" d="M572.5 241.4C518.3 135.6 410.9 64 288 64S57.7 135.6 3.5 241.4a32.4 32.4 0 0 0 0 29.2C57.7 376.4 165.1 448 288 448s230.3-71.6 284.5-177.4a32.4 32.4 0 0 0 0-29.2zM288 400a144 144 0 1 1 144-144 143.9 143.9 0 0 1 -144 144zm0-240a95.3 95.3 0 0 0 -25.3 3.8 47.9 47.9 0 0 1 -66.9 66.9A95.8 95.8 0 1 0 288 160z"/>
                        </svg>
                      </button>
                    </li>
                  </ul>

                  <div v-else class="dashboard-empty">
                    No recent quotes.
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== PRICING NOTICE =====
                 Sits inside col-lg-8 directly below the
                 Completed Orders / Completed Quotes row so the
                 disclaimer is visually anchored to the cards
                 it pertains to. Was previously below the entire
                 dashboard-upper-grid which made it look like a
                 page-level footer disclaimer rather than
                 specifically about re-ordering from the
                 completed cards. -->
            <div class="dashboard-table-notice"
                 v-if="companySelected">
              *Pricing is subject to change on re-orders.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== RE-ORDER COMPANY MISMATCH PROMPT (Feature 1) =====
         Shown when the user clicks Re-Order on a Completed Order
         that was placed by a DIFFERENT company than the one they
         currently have selected. Offers two explicit choices:
           1. Add to the ORDER's company (the always-valid path —
              switches to that company's cart via the existing
              /cart/reorder backend).
           2. Add to MY company (cross-company: clones the order's
              parts into a NEW cart under the CURRENT company, no
              company switch). May fail if the parts don't belong
              to the current company's machines.
         When the order's company matches the current one, this
         modal never opens — onReorderClick goes straight to the
         existing reorder() path. -->
    <b-modal
      id="reorderMismatchModal"
      v-model="showReorderMismatchModal"
      title="Re-order for a different company"
      hide-footer
      centered
    >
      <!-- CHOICE STATE — the unchanged two-option prompt. Hidden once an
           "Add to my company" attempt fails (reorderFailState set), which
           swaps in the failure panel below. -->
      <div class="reorder-mismatch-body" v-if="!reorderFailState">
        <p class="reorder-mismatch-lead">
          This order was placed by a different company.
        </p>
        <ul class="reorder-mismatch-facts">
          <li>
            Placed by:
            <strong>{{ mismatchOrderCompanyDisplay }}</strong>
          </li>
          <li>
            You're in:
            <span>{{ mismatchCurrentCompanyDisplay }}</span>
          </li>
        </ul>
        <p class="reorder-mismatch-help">
          Which company should this re-order be added to?
        </p>
        <div class="reorder-mismatch-actions">
          <button
            type="button"
            class="btn btn-primary reorder-mismatch-btn"
            :disabled="reorderMismatchBusy"
            @click="reorderForOrderCompany"
          >
            Add to {{ mismatchOrderCompanyName }}
          </button>
          <button
            type="button"
            class="btn btn-outline-dark reorder-mismatch-btn"
            :disabled="reorderMismatchBusy"
            @click="reorderForMyCompany"
          >
            Add to my company ({{ mismatchCurrentCompanyName }})
          </button>
        </div>
        <p class="reorder-mismatch-caveat">
          Adding to your company copies the parts from the original order. If
          any of those parts belong to the other company's machines, they may
          not be valid for your company.
        </p>
      </div>

      <!-- FAILURE STATE — shown when "Add to my company" was rejected by the
           backend. Surfaces the REAL error, the specific machine(s) /
           assembly / wear part(s) that aren't set up under the current
           company (diffed from the account setup), a plain-English suggested
           fix, and (Master only) an action to add the missing items + retry. -->
      <div class="reorder-mismatch-body reorder-fail" v-else>
        <p class="reorder-fail-title">
          <span aria-hidden="true">⚠</span>
          Couldn't add this order to {{ mismatchCurrentCompanyName }}
        </p>

        <p class="reorder-fail-reason">{{ reorderFailReason }}</p>

        <!-- Missing items + suggested fix (when we could identify them) -->
        <div v-if="reorderMissing.length" class="reorder-fail-missing">
          <p class="reorder-fail-subhead">
            These items from the order aren't set up for
            {{ mismatchCurrentCompanyName }}:
          </p>
          <ul class="reorder-fail-list">
            <li v-for="m in reorderMissing" :key="m.key">
              <span class="reorder-fail-kind">{{ m.kindLabel }}</span>
              <span class="reorder-fail-name">{{ m.label }}</span>
              <span class="reorder-fail-fix">{{ m.fix }}</span>
            </li>
          </ul>
        </div>
        <div v-else-if="reorderFailDiagnosing" class="reorder-fail-diagnosing">
          Checking which items are missing…
        </div>

        <!-- Add-missing action (Master only, and only when add-able items
             were found). Two choices: add permanently (for future orders &
             quotes), or add "just this time" (auto-removed after this order is
             placed / quote is finalized). On success we offer a retry. -->
        <div v-if="canAddMissing" class="reorder-fail-actions">
          <button
            type="button"
            class="btn btn-primary reorder-mismatch-btn"
            :disabled="reorderMismatchBusy"
            @click="addMissingToMyCompany(false)"
          >
            {{ reorderMismatchBusy
              ? 'Adding…'
              : ('Add ' + addableMissingSummary + ' for future orders & quotes') }}
          </button>
          <button
            type="button"
            class="btn btn-outline-dark reorder-mismatch-btn"
            :disabled="reorderMismatchBusy"
            @click="addMissingToMyCompany(true)"
          >
            {{ reorderMismatchBusy ? 'Adding…' : 'Add just this time' }}
          </button>
          <p class="reorder-fail-temp-note">
            “Just this time” adds {{ addableMissingSummary }} to
            {{ mismatchCurrentCompanyName }} so you can place this re-order now.
            Removable later in Account Setup; auto-removed after this order is
            placed / quote is finalized.
          </p>
        </div>

        <!-- Retry affordance — appears after the missing items were added. -->
        <div v-if="reorderCanRetry" class="reorder-fail-retry">
          <p class="reorder-fail-retry-note">
            <span aria-hidden="true">✓</span> Added to
            {{ mismatchCurrentCompanyName }}. You can re-try the re-order now.
          </p>
          <button
            type="button"
            class="btn btn-primary reorder-mismatch-btn"
            :disabled="reorderMismatchBusy"
            @click="retryReorderForMyCompany"
          >
            {{ reorderMismatchBusy ? 'Re-trying…' : 'Re-try “Add to my company”' }}
          </button>
        </div>

        <!-- Always offer the always-valid order's-company path as a way out. -->
        <div class="reorder-fail-alt">
          <button
            type="button"
            class="btn btn-outline-dark reorder-mismatch-btn"
            :disabled="reorderMismatchBusy"
            @click="reorderForOrderCompany"
          >
            Add to {{ mismatchOrderCompanyName }} instead
          </button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import FeApiService from "@/assets/js/FeApiService";
import { addTempSetupAdds } from "@/assets/js/tempSetupAdds";
import SelectCompanyPrompt from "@/components/Common/SelectCompanyPrompt.vue";
import Vue from "vue";
import Vue2Filters from "vue2-filters";
// TopPartsWidget is globally registered in main.js (next to
// CartWidget) so it's usable as <TopPartsWidget> on any host
// without a per-page import. See main.js for the registration.

const apiService = new FeApiService();

Vue.use(Vue2Filters);

export default {
  name: "index",
  components: { SelectCompanyPrompt },
  computed: {
    ...mapGetters({ currentUser: "currentUser" }),
    // Defensively detects "master"/admin-role users across several possible
    // shapes of the currentUser object (isAdmin/isMaster booleans, a single
    // role string, or a roles[] array containing strings or {name} objects).
    // Matches are case-insensitive on "master" or "admin".
    isMasterUser() {
      const u = this.currentUser || {};
      if (u.isAdmin === true || u.isMaster === true) return true;
      const matchesRole = function (val) {
        if (!val) return false;
        const s =
          typeof val === "string"
            ? val
            : (val && val.name) || (val && val.role) || "";
        const low = String(s).toLowerCase();
        return low === "master" || low === "admin";
      };
      if (matchesRole(u.role)) return true;
      if (Array.isArray(u.roles) && u.roles.some(matchesRole)) return true;
      return false;
    },
    insightsPeriodLabel() {
      return this.insightsPeriod === "ytd" ? "YTD" : "All Time";
    },
    processedOrderCount() {
      return Number(this.insights.ytdOrderCount || 0);
    },
    savedOrderCount() {
      return Number(this.insights.savedOrderCount || 0);
    },
    processedQuoteCount() {
      return Number(this.insights.processedQuoteCount || this.insights.quoteCount || 0);
    },
    savedQuoteCount() {
      return Number(this.insights.savedQuoteCount || 0);
    },
    orderActivityTotal() {
      if (typeof this.insights.orderActivityCount !== "undefined") {
        return Number(this.insights.orderActivityCount || 0);
      }
      return this.processedOrderCount + this.savedOrderCount;
    },
    quoteActivityTotal() {
      if (typeof this.insights.quoteActivityCount !== "undefined") {
        return Number(this.insights.quoteActivityCount || 0);
      }
      return this.processedQuoteCount + this.savedQuoteCount;
    },
    savedCartTotal() {
      const computedTotal = this.savedOrderCount + this.savedQuoteCount;
      if (computedTotal > 0) {
        return computedTotal;
      }
      return Number(this.insights.savedCartCount || 0);
    },

    // ===== STAGING TRAY + MODAL COMPUTEDS =====
    // Direct ports of the same computeds on Search.vue. Same
    // semantics, same shape — { orderNumber, poNumber, parts[] }
    // — so the staging-tray template is byte-identical between
    // Account and Search. Order in which POs were first added
    // determines orderNumber, which is just a 1-based display
    // index; the persistent identifier is poNumber.
    groupedSelections() {
      var groups = {};
      var poOrder = [];
      (this.selectedParts || []).forEach(function (p) {
        var po = p.poNumber || "(no PO)";
        if (!groups[po]) {
          groups[po] = { poNumber: po, parts: [] };
          poOrder.push(po);
        }
        groups[po].parts.push(p);
      });
      return poOrder.map(function (po, i) {
        return {
          orderNumber: i + 1,
          poNumber: groups[po].poNumber,
          parts: groups[po].parts,
        };
      });
    },
    selectedPartsCount() {
      return (this.selectedParts || []).length;
    },
    groupedQuoteSelections() {
      var groups = {};
      var order = [];
      (this.selectedPartsQuote || []).forEach(function (p) {
        var po = p.poNumber || "(no PO)";
        if (!groups[po]) {
          groups[po] = { poNumber: po, parts: [] };
          order.push(po);
        }
        groups[po].parts.push(p);
      });
      return order.map(function (po, i) {
        return {
          orderNumber: i + 1,
          poNumber: groups[po].poNumber,
          parts: groups[po].parts,
        };
      });
    },
    selectedQuotePartsCount() {
      return (this.selectedPartsQuote || []).length;
    },
    // Drives v-if on the entire staging-tray column. The user
    // wants the column hidden when both queues are empty
    // (rather than showing a persistent empty-state placeholder)
    // so the dashboard reads as cart-driven: cart empty → no
    // staging panel visible. Once syncFromActiveCart has populated
    // selectedParts from the live cart contents, this flips true
    // and the column appears with the actual cart contents inside.
    hasStagingItems() {
      return (
        (this.selectedParts && this.selectedParts.length > 0) ||
        (this.selectedPartsQuote && this.selectedPartsQuote.length > 0)
      );
    },

    // Total count of parts across both queues — order and quote.
    // Drives the live badge on the cart's side-edge pill so the
    // user can see at a glance how many parts they have parked
    // while the inline cart is collapsed. Sum (not max) because
    // the two queues are independent collections of distinct
    // parts; "5 items in your cart" should mean order+quote
    // combined.
    cartPillCount() {
      return this.selectedPartsCount + this.selectedQuotePartsCount;
    },

    // Flat array of partIds across both staging queues, fed to
    // TopPartsWidget as :staged-part-ids. The widget uses it to
    // flip each row's icon from cart (add) → pencil (edit) when
    // the corresponding part is already queued in the user's
    // cart. Mirrors the same is-edit behaviour the old inline
    // markup had via isPartStaged(), without leaking host
    // queue internals into the widget itself.
    stagedPartIdsForWidget() {
      var ids = [];
      var orderQueue = this.selectedParts || [];
      for (var i = 0; i < orderQueue.length; i++) {
        if (orderQueue[i] && orderQueue[i].partId != null) {
          ids.push(orderQueue[i].partId);
        }
      }
      var quoteQueue = this.selectedPartsQuote || [];
      for (var j = 0; j < quoteQueue.length; j++) {
        if (quoteQueue[j] && quoteQueue[j].partId != null) {
          ids.push(quoteQueue[j].partId);
        }
      }
      return ids;
    },

    addToCartOkTitle() {
      return this.pickerMode === "edit" ? "Update Cart" : "Add to Cart";
    },
    // The staging groups that apply to the currently chosen
    // destination. Drives the modal's PO picker — each group is
    // a one-click target for the user to attach the new part to.
    currentDestinationGroups() {
      if (!this.pendingDestination) return [];
      return this.pendingDestination === "quote"
        ? this.groupedQuoteSelections
        : this.groupedSelections;
    },
    // Drives :ok-disabled on the modal so the Add/Update button
    // can't be clicked until the user has supplied everything we
    // need. Edit mode skips the destination check (it's locked).
    canConfirm() {
      if (this.pickerMode !== "edit" && !this.pendingDestination) {
        return false;
      }
      var po = (this.pendingPoNumber || "").trim();
      if (!po) return false;
      var qty = Number(this.pendingQuantity);
      if (!isFinite(qty) || qty < 1) return false;
      return true;
    },

    // ===== RE-ORDER MISMATCH DISPLAY HELPERS (Feature 1) =====
    // The order's company NAME alone (no id) for the "Add to
    // [Order's Company]" button label. Falls back to a generic
    // label if the order has no company name.
    mismatchOrderCompanyName() {
      var o = this.pendingReorderOrder;
      var c = o && o.company;
      return (c && c.name) ? c.name : "the order's company";
    },
    // Full "Name – (CODE)" / "Name – ID" label for the order's
    // company, shown in the modal's "Placed by" line.
    mismatchOrderCompanyDisplay() {
      return this.pendingReorderOrder
        ? this.orderCompanyDisplay(this.pendingReorderOrder)
        : "";
    },
    // The CURRENT company's NAME (just the name, no id/code) for
    // the "Add to my company (…)" button label.
    mismatchCurrentCompanyName() {
      return this.currentCompanyNameOnly();
    },
    // Full "Name – (CODE)" label for the CURRENT company, shown in
    // the modal's "You're in" line.
    mismatchCurrentCompanyDisplay() {
      return this.currentCompanyDisplay();
    },

    // The missing items that CAN be added straight from here — machines
    // and wear parts (setup-link-add supports both). Assemblies are NOT
    // directly addable (they belong to the catalog under a machine), so
    // they're listed for the suggested fix but excluded from the action.
    addableMissing() {
      return (this.reorderMissing || []).filter(function (m) {
        return m.kind === "machine" || m.kind === "wear";
      });
    },
    // Master-only (the setup APIs are Master-gated) AND we found at least
    // one add-able missing item AND we haven't already added them.
    canAddMissing() {
      return (
        this.isMasterUser &&
        !this.reorderCanRetry &&
        this.addableMissing.length > 0
      );
    },
    // Compact "2 machines + 1 wear part" summary for the add button label.
    addableMissingSummary() {
      var machines = this.addableMissing.filter(function (m) {
        return m.kind === "machine";
      }).length;
      var wears = this.addableMissing.filter(function (m) {
        return m.kind === "wear";
      }).length;
      var parts = [];
      if (machines)
        parts.push(machines + (machines > 1 ? " machines" : " machine"));
      if (wears)
        parts.push(wears + (wears > 1 ? " wear parts" : " wear part"));
      return parts.join(" + ") || "the missing items";
    },
  },
  data() {
    return {
      companySelected: false,
      currentCompanyId: "",
      companyCheckInterval: null,

      // ===== COMPANY LIST (for display names — Features 1/3/4) =====
      // Same source the header uses (getCompanyList →
      // /user/GetCompanySelectItems): an array of
      // { value: "<companyId>", text: "Name - (CODE)" }. Loaded on
      // mount. Used to resolve the CURRENT company's display label
      // (the entry whose value == localStorage.companyId) for the
      // mismatch prompt. The order's own company name/code come off
      // the order object (o.company.*); this list is the fallback /
      // current-company source.
      companies: [],

      // Per-order expand state for the Completed Orders list (Feature
      // 4). Keyed by order id; true = the order's company line is
      // revealed. Object (not Set) so Vue 2 reactivity tracks it via
      // $set in toggleOrderCompany.
      expandedOrderCompany: {},

      // ===== RE-ORDER MISMATCH PROMPT (Feature 1) =====
      // showReorderMismatchModal drives the b-modal. pendingReorderOrder
      // is the full order object the user clicked Re-Order on (kept so
      // both choice handlers can read its id + company + parts).
      // reorderMismatchBusy disables both buttons while a request is in
      // flight so a double-click can't fire two reorders.
      showReorderMismatchModal: false,
      pendingReorderOrder: null,
      reorderMismatchBusy: false,

      // ===== RE-ORDER "ADD TO MY COMPANY" FAILURE STATE =====
      // reorderFailState — when truthy, the modal swaps the two-choice
      //   prompt for the failure panel (the cross-company save was
      //   rejected). Reset to false each time the modal/choice reopens.
      // reorderFailReason — the REAL backend message (title/detail) we
      //   surfaced from the rejected saveCart, instead of a generic toast.
      // reorderMissing — [{ key, kind:'machine'|'assembly'|'wear',
      //   kindLabel, label, fix, refId, partId }] computed by diffing the
      //   order's purchaseOrders against this company's account setup
      //   (getAccountSetup). Drives the "what's missing + suggested fix"
      //   list and the add-missing action.
      // reorderFailDiagnosing — true while we fetch + diff the setup.
      // reorderCanRetry — true once the missing items were added, so the
      //   panel offers a one-click re-try of "Add to my company".
      reorderFailState: false,
      reorderFailReason: "",
      reorderMissing: [],
      reorderFailDiagnosing: false,
      reorderCanRetry: false,
      // Guards the best-effort failure event-log POST so a single
      // cross-company-reorder failure logs at most once even though the
      // catch handler and the async diagnosis both reach the logger.
      reorderFailLogged: false,
      // Once-guard for the SUCCESS event-log POST so a single reorder logs at
      // most one success event (the three reorder entry points + retry all
      // route through logReorderSuccessEvent). Reset alongside reorderFailLogged
      // whenever a fresh reorder attempt opens (resetReorderFailState) and also
      // before each same-company reorder() / onReorderClick dispatch.
      reorderSuccessLogged: false,
      sortBy: "dateCreated|desc",
      // Expansion state for the two collapsible Important
      // Information items. Both default to false (collapsed)
      // so the user sees only the headline + "(+) Click the (+)
      // to see more" hint until they explicitly open one. Keys
      // identify the message type for stable bookkeeping —
      // serviceWarning maps to the "Service Portal should never
      // be used for breakdown parts" headline, banking maps to
      // the "Steele has updated its banking information"
      // headline.
      expandedMessages: {
        serviceWarning: false,
        banking: false,
      },

      // ===== CART SIDE-RAIL TOGGLE STATE =====
      // Host-controlled toggle that parks the cart staging tray
      // as a side-edge pill, freeing the col-lg-4 it would have
      // occupied. Persisted to localStorage. NOTE: this is
      // Account-page-local; the cart staging tray here is custom
      // markup, NOT the shared CartWidget. (CartWidget is used
      // on /account/savedcarts, OrderHistory, etc. and has its
      // own collapse state independent of this.)
      //
      // When `cartCollapsedToRail` is true:
      //   - The inline cart column (acct-important-cart-col)
      //     does NOT render — col-lg-8 falls back to col-12
      //   - A pill appears at the right edge; clicking it
      //     flips this flag back to false (expand)
      cartCollapsedToRail: false,

      // ===== TOP 5 INLINE-VISIBILITY MIRROR =====
      // Mirrors what the embedded TopPartsWidget reports via its
      // inline-visibility-change event. Drives the col-lg-8 ↔
      // col-12 swap on the insights left column so the insights
      // stats + completed cards fill the row when the widget
      // collapses to its side pill.
      //
      // Defaults to true (widget is inline). The widget emits
      // an immediate event on mount so this value reconciles
      // to the user's last-saved choice before first paint.
      // Unlike cartCollapsedToRail above, we don't store this
      // in localStorage ourselves — the WIDGET owns the
      // persistence (jcsteele.topparts.collapsed), and we just
      // listen to what it tells us.
      topPartsInlineVisible: true,

      rows: 0,
      currentPage: 1,
      perPage: 5,
      resolvingTopPart: false,
      resolvingTopPartMessage: "Finding the correct part context...",
      ordersFields: [
        { key: "dateCreated", label: "Date" },
        { key: "id", label: "Acknowledgement #" },
        { key: "cost", label: "Order Total" },
        "status",
        { key: "reorder", label: "Re-Order*" },
      ],
      orders: [],
      quotesFields: [
        { key: "dateCreated", label: "Date" },
        { key: "id", label: "Quote #" },
        { key: "cost", label: "Quote Total" },
        { key: "reorder", label: "Re-Order*" },
      ],
      quotes: { data: [] },
      insights: {
        ytdOrderCount: 0,
        ytdTotalSpend: 0,
        processedQuoteCount: 0,
        savedOrderCount: 0,
        savedQuoteCount: 0,
        orderActivityCount: 0,
        quoteActivityCount: 0,
        topParts: [],
      },
      insightsPeriod: "ytd",
      // Whose activity the insights + Completed Orders/Quotes reflect:
      //   "account" → everyone on the selected company (the whole account)
      //   "mine"    → only orders/quotes this user placed
      // Defaults to the whole account so the dashboard isn't empty for users
      // whose teammates place the orders. Persisted across visits.
      accountScope:
        localStorage.getItem("jcsteele.dashboard.accountScope") === "mine"
          ? "mine"
          : "account",
      topPartsFields: [
        { key: "partCode", label: "Part Code" },
        { key: "description", label: "Description" },
        { key: "totalQuantity", label: "Total Qty Ordered" },
        { key: "timesOrdered", label: "Times Ordered" },
        { key: "addToCart", label: "" },
      ],

      // ===== STAGING TRAY + ADD-TO-CART MODAL STATE =====
      // Full port of the data shape on Search.vue. selectedParts
      // and selectedPartsQuote are the two queues — order-bound
      // and quote-bound. They persist to localStorage under
      // jcsteele.insights.selections.<companyId> and
      // jcsteele.insights.selections.rfq.<companyId> respectively,
      // matching Search.vue's keys so the queues round-trip
      // between the two pages.
      //
      // committedSnapshot / committedSnapshotQuote track what's
      // currently in the linked server-side cart. The delta
      // between (selectedParts, committedSnapshot) tells the
      // sync code what's been added, removed, or qty-edited
      // since the last save — see syncQueueToCart for the full
      // delta logic.
      //
      // addingInsightPart is a guard against concurrent saves;
      // the _syncTimer / _syncTimerQuote / _syncDirty /
      // _syncDirtyQuote / _suppressCartNav fields are non-
      // reactive instance state (set in mounted on `this`) used
      // by the debounced sync layer. They're not in data()
      // because Vue reactivity doesn't help here — they're
      // mutable refs, not view-binding state.
      selectedParts: [],
      selectedPartsQuote: [],
      committedSnapshot: null,
      committedSnapshotQuote: null,
      addingInsightPart: false,

      // Modal state — same shape as Search.vue.
      showAddToCartModal: false,
      pendingPart: null,
      pendingDestination: "order",
      pendingPoNumber: "",
      pendingQuantity: 1,
      pickerMode: "add", // 'add' | 'edit'
      useNewPo: false,
    };
  },
  mounted() {
    // Restore the cart / Top 5 side-rail collapse flags from
    // localStorage before anything else runs, so the very
    // first render reflects the user's stored preference and
    // we don't flash inline content that's about to be
    // hidden (or vice versa).
    this.restoreSideRailState();

    // Load the company select-list (same source the header uses) so
    // the reorder mismatch prompt + expandable can resolve the
    // CURRENT company's display name. Non-blocking — the order's own
    // company name comes off the order object regardless.
    this.getCompanies();

    if (localStorage.companyId) {
      this.companySelected = true;
      this.getOrders();
      this.getQuotes();
      this.getInsights();
    }

    this.currentCompanyId = localStorage.companyId || "";

    // ===== HYDRATE STAGING QUEUES =====
    // Two-step hydration mirrors Search.vue:
    //   1. Pull from localStorage (instant, even if offline)
    //   2. Sync from active server cart (authoritative, async)
    //
    // The cart sync runs second so it overrides the localStorage
    // restore when the server has a different / fresher view.
    // When no active cart exists, the sync clears the local queue
    // (and its localStorage tag) so the staging tray hides via
    // hasStagingItems → v-if. This is the fix for the bug where
    // prior Search-page staging was leaking into Account even
    // though the user's actual cart was empty.
    this.loadSelectionsFromStorage();
    this.loadSelectionsFromStorageQuote();
    this.syncFromActiveCart();
    this.syncFromActiveQuoteCart();

    // ===== CART-BUS LISTENER =====
    // Head.vue and the cart pages emit `cart-updated` whenever
    // the server-side cart changes (placed order, cleared cart,
    // saved cart, parts added/removed via Search/Cart pages).
    // Listening here keeps the staging tray in lockstep with
    // those changes — if the user clears the cart on /cart and
    // then navigates back to Account, the tray refreshes.
    this._cartBusHandler = () => {
      this.syncFromActiveCart();
      this.syncFromActiveQuoteCart();
    };
    if (Vue.prototype.$cartBus) {
      Vue.prototype.$cartBus.$on("cart-updated", this._cartBusHandler);
    }

    // ===== COMPANY-CHANGE BUS LISTENER (Issue B) =====
    // The header company selector (Head.vue selectCompany) sets
    // localStorage.companyId and emits `company-changed` on $cartBus. The
    // dashboard's Completed Orders / Completed Quotes / insights are all
    // company-scoped (their endpoints filter by companyId), so they MUST
    // refetch for the newly-selected company. Listening to the explicit event
    // refetches immediately and reliably — it no longer depends solely on the
    // 500ms companyCheckInterval poll below, and it still works in the edge
    // case where Head's window.location.reload() is skipped (e.g. a corrupt
    // cart_company_* JSON throws before the reload). When the reload DOES fire,
    // this component is torn down and remounts fresh — harmless. Reuses the
    // same applyCompanyChange() path the poll uses so behavior is identical.
    this._companyChangedHandler = (cid) => {
      const newCompanyId =
        cid != null && cid !== "" ? String(cid) : localStorage.companyId || "";
      this.applyCompanyChange(newCompanyId);
    };
    if (Vue.prototype.$cartBus) {
      Vue.prototype.$cartBus.$on("company-changed", this._companyChangedHandler);
    }

    // Polling fallback: still watch localStorage.companyId directly so a
    // company change that somehow bypasses the bus event (or a different tab /
    // programmatic write) is still picked up. applyCompanyChange no-ops when
    // the id hasn't actually changed, so the poll and the bus event can't
    // double-fetch.
    this.companyCheckInterval = setInterval(() => {
      this.applyCompanyChange(localStorage.companyId || "");
    }, 500);
  },
  // ===== STAGING QUEUE → STORAGE WATCHERS =====
  // Mirror Search.vue's watch-and-persist pattern. Whenever the
  // queue changes (modal confirm, × remove, qty edit via the
  // tray's number input), bump the storage so a refresh or
  // navigation away preserves the queue.
  watch: {
    selectedParts: {
      handler() {
        this.saveSelectionsToStorage();
      },
      deep: true,
    },
    selectedPartsQuote: {
      handler() {
        this.saveSelectionsToStorageQuote();
      },
      deep: true,
    },
  },
  beforeDestroy() {
    if (this.companyCheckInterval) {
      clearInterval(this.companyCheckInterval);
    }
    // Detach the cart-bus listener so a stale closure doesn't
    // keep firing into a destroyed component (would NRE on
    // selectedParts since reactivity is gone).
    if (Vue.prototype.$cartBus && this._cartBusHandler) {
      Vue.prototype.$cartBus.$off("cart-updated", this._cartBusHandler);
    }
    // Detach the company-change listener for the same reason.
    if (Vue.prototype.$cartBus && this._companyChangedHandler) {
      Vue.prototype.$cartBus.$off("company-changed", this._companyChangedHandler);
    }
  },
  methods: {
    // ===== SCOPED COMPANY ID (anti "all-companies" leak) =====
    // The dashboard's Completed Orders / Completed Quotes / insights are always
    // scoped to the ONE selected company (there is no "all companies" mode here
    // — the only toggle is accountScope mine/account, which the backend ignores
    // for Masters). For a Master, sending an EMPTY companyId makes the backend
    // drop the company filter entirely and return EVERY company's rows
    // (OrderController.Get only adds CompanyId when companyId > 0, with no user
    // filter for a Master). So an empty id silently means "all companies".
    //
    // localStorage.companyId is normally kept populated by Head.vue, but this
    // is the defensive backstop: prefer localStorage.companyId, then fall back
    // to this.currentCompanyId (which the company-changed event / poll keep in
    // sync). Only returns "" when we genuinely have no company at all — in which
    // case the callers' own guards (companySelected / the mounted if-guard)
    // keep the lists empty rather than firing an unscoped fetch.
    scopedCompanyId() {
      return localStorage.companyId || this.currentCompanyId || "";
    },
    // ===== COMPANY-CHANGE REFRESH (Issue B) =====
    // Single source of truth for reacting to a selected-company change.
    // Invoked BOTH by the company-changed $cartBus event (immediate, from the
    // header selector) and by the 500ms companyCheckInterval poll (fallback).
    // No-ops when the id hasn't actually changed vs this.currentCompanyId, so
    // the two callers can't double-fetch. When the company changed it persists
    // the outgoing company's staging queues, then refetches the company-scoped
    // dashboard data (Completed Orders / Completed Quotes / insights) and
    // re-hydrates the new company's staging queues — the exact behavior the
    // poll had before, just extracted so the event path reuses it verbatim.
    applyCompanyChange(rawCompanyId) {
      const newCompanyId = rawCompanyId || "";
      if (newCompanyId === this.currentCompanyId) {
        return;
      }
      // Persist the OUTGOING company's queue under its own key before
      // this.currentCompanyId moves on — same pattern as Search.vue's
      // companyId watcher. Without this, switching companies would lose any
      // pending parts the user hadn't committed yet for the outgoing company.
      if (this.currentCompanyId) {
        this.saveSelectionsToStorage(this.currentCompanyId);
        this.saveSelectionsToStorageQuote(this.currentCompanyId);
      }
      this.currentCompanyId = newCompanyId;
      if (newCompanyId) {
        this.companySelected = true;
        // Refetch the company-scoped dashboard cards for the NEW company.
        this.getOrders();
        this.getQuotes();
        this.getInsights();
        // Hydrate the new company's queues from its own storage slots, then
        // sync from the new company's active cart. Same two-step pattern as
        // the initial mounted() flow.
        this.loadSelectionsFromStorage();
        this.loadSelectionsFromStorageQuote();
        this.syncFromActiveCart();
        this.syncFromActiveQuoteCart();
      } else {
        this.companySelected = false;
        this.orders = [];
        this.quotes = { data: [] };
        this.insights = {
          ytdOrderCount: 0,
          ytdTotalSpend: 0,
          processedQuoteCount: 0,
          savedOrderCount: 0,
          savedQuoteCount: 0,
          orderActivityCount: 0,
          quoteActivityCount: 0,
          topParts: [],
        };
        this.selectedParts = [];
        this.selectedPartsQuote = [];
      }
    },

    // ===== SIDE-RAIL TOGGLE HELPERS =====
    // Flip the cart's collapsed-to-rail state. When toggled
    // ON, the inline acct-important-cart-col column stops
    // rendering and a side-edge pill appears so the cart is
    // still one click away. When toggled OFF, the inline
    // column comes back. Persists to localStorage so the
    // user's choice survives navigation and reloads.
    toggleCartCollapsedToRail() {
      this.cartCollapsedToRail = !this.cartCollapsedToRail;
      try {
        localStorage.acctCartCollapsedToRail = this.cartCollapsedToRail
          ? "1"
          : "0";
      } catch (e) {
        // localStorage may be unavailable (private mode, quota,
        // etc.). The toggle still works for the current session;
        // it just won't persist. Swallow silently.
      }
    },

    // ===== TOP 5 WIDGET HANDLERS =====
    // The widget owns its own collapse state and pill rendering.
    // We just receive notifications and decide what to do
    // layout-wise (col-lg-8 ↔ col-12 swap) plus route part-click
    // events into Account's existing add-to-cart flow.
    onTopPartsInlineVisibilityChange(visible) {
      this.topPartsInlineVisible = !!visible;
    },
    onTopPartsWidgetPartClick(payload) {
      // Re-shape into the call site addTopPartToCart already
      // expects. The widget emits `{ part }`, and the existing
      // host method takes the raw part argument — passing it
      // through preserves all existing add-to-cart routing
      // (add vs edit detection, modal pre-fill, etc.) without
      // duplicating that logic in the widget.
      if (payload && payload.part) {
        this.addTopPartToCart(payload.part);
      }
    },

    // Restore the cart's side-rail collapse flag from
    // localStorage on mount so the user's last choice
    // persists across reloads and route changes. Defaults
    // to expanded inline (false) when no value is stored.
    // Top 5 restoration is handled inside TopPartsWidget itself
    // (via its own COLLAPSE_STORAGE_KEY) — we don't need to
    // duplicate that hydration here.
    restoreSideRailState() {
      try {
        if (localStorage.acctCartCollapsedToRail === "1") {
          this.cartCollapsedToRail = true;
        }
      } catch (e) {
        /* localStorage unavailable — keep defaults */
      }
    },

    getQuotes() {
      apiService
        .getQuotes({
          page: 1,
          per_page: 5,
          sortBy: this.sortBy,
          companyId: this.scopedCompanyId(),
          accountWide: this.accountScope === "account",
        })
        .then((request) => {
          this.quotesSuccessful(request);
        })
        .catch((err) => this.quotesFailed(err));
    },
    quotesSuccessful(request) {
      this.quotes = request || { data: [] };
    },
    quotesFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    getOrders() {
      apiService
        .getOrders({
          page: 1,
          per_page: 5,
          sortBy: this.sortBy,
          filter: "",
          poNumber: "",
          companyId: this.scopedCompanyId(),
          accountWide: this.accountScope === "account",
        })
        .then((request) => {
          this.ordersSuccessful(request);
        })
        .catch((err) => this.ordersFailed(err));
    },
    ordersSuccessful(request) {
      this.rows = request.total;
      this.currentPage = request.current_page;
      this.perPage = request.per_page;
      this.orders = Array.from(request.data[0] || []);
      // Admin-only scope diagnostic (replaces the old on-screen DEBUG toast):
      // records to Account Setup → Events & Logs whether the dashboard's orders
      // scoped correctly to the selected company. Customers never see it.
      this.recordOrderScopeEvent(request);
    },

    // Records a Master-only event into Account Setup → Events & Logs capturing
    // whether the dashboard's Completed Orders scoped to the SELECTED company —
    // success ("scope OK") or anomaly ("cross-company leak"), each with a human
    // explanation of the root cause so anyone reading the log understands it.
    // Replaces the temporary on-screen debug toast: admins see it in events,
    // customers never do. Deduped per (company + ok/anomaly state) per browser
    // session so it records the state without flooding the log. recordSetupEvent
    // is Master-only (non-masters get 403, swallowed) — the intended admin-only
    // visibility. Never throws: a diagnostic must not break the page.
    recordOrderScopeEvent(request) {
      try {
        var selCid = Number(this.scopedCompanyId()) || 0;
        var total = (request && request.total) || 0;
        var returned = (this.orders && this.orders.length) || 0;
        // Distinct companies present in the returned page.
        var companies = {};
        (this.orders || []).forEach(function (o) {
          if (o && o.company && o.company.id != null) {
            companies[o.company.id] =
              (o.company.name || "Company") + " (#" + o.company.id + ")";
          }
        });
        var otherCompanies = Object.keys(companies).filter(function (id) {
          return selCid && String(id) !== String(selCid);
        });
        // OK when no company is selected, or every returned order belongs to it.
        var scopeOk = !selCid || otherCompanies.length === 0;
        var coName = this.currentCompanyNameOnly
          ? this.currentCompanyNameOnly()
          : "";

        // Dedup: one event per (company | ok-state) per session.
        var dedupKey =
          "jcs_orderScopeEvt_" + selCid + "_" + (scopeOk ? "ok" : "anom");
        try {
          if (sessionStorage.getItem(dedupKey)) return;
          sessionStorage.setItem(dedupKey, "1");
        } catch (e) {
          /* private mode / no storage — fall through and record */
        }

        var explanation =
          "What this checks: the dashboard's Completed Orders must show ONLY the " +
          "selected company's orders. The backend (OrderController.Get) scopes by " +
          "companyId. Root cause of the earlier bug (recorded so a human can " +
          "recognize a regression): the company filter was appended to model.Search " +
          "as a SEPARATE list element, but the repository's Find/BuildSearch only " +
          "apply the combined predicate at model.Search[0] — so the extra element was " +
          "silently ignored and EVERY company's orders came back. Fix: AND the company " +
          "filter INTO model.Search[0]. An ANOMALY here means that filter regressed.";

        var detail;
        if (scopeOk) {
          detail =
            "Dashboard order scope OK for " +
            (coName || "company #" + selCid) +
            ": requested companyId=" +
            (selCid || "(none)") +
            ", scope=" +
            this.accountScope +
            ", returned " +
            returned +
            " of " +
            total +
            " — all returned orders belong to the selected company. " +
            explanation;
        } else {
          var coList = Object.keys(companies)
            .map(function (id) {
              return companies[id];
            })
            .join(", ");
          detail =
            "Dashboard order scope ANOMALY for " +
            (coName || "company #" + selCid) +
            ": requested companyId=" +
            selCid +
            ", scope=" +
            this.accountScope +
            ", returned " +
            returned +
            " of " +
            total +
            ", but the results include OTHER companies: " +
            coList +
            ". The per-company order filter is NOT being applied (cross-company leak). " +
            explanation;
        }

        apiService
          .recordSetupEvent({
            kind: scopeOk
              ? "dashboard-order-scope-ok"
              : "dashboard-order-scope-anomaly",
            title: scopeOk
              ? "Dashboard order scope OK"
              : "Dashboard order scope ANOMALY (cross-company)",
            detail: detail,
            companyId: selCid,
            company: coName,
            success: scopeOk,
            data: {
              requestedCompanyId: String(selCid),
              scope: this.accountScope,
              returnedCount: String(returned),
              totalCount: String(total),
              companiesInResult: Object.keys(companies)
                .map(function (id) {
                  return companies[id];
                })
                .join("; "),
            },
          })
          .catch(function () {
            /* Master-only endpoint; non-masters 403 — swallow */
          });
      } catch (e) {
        /* diagnostic only — never break the dashboard */
      }
    },
    ordersFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    // ===== reorder =====
    // Mirror OrderDetails.vue's working Re-Order button: POST
    // /cart/reorder/{id} (the backend copies the order's items into
    // a fresh cart and clears PO Numbers), then route to the Cart
    // page with reorder:true so it auto-opens the Modify modal where
    // the customer makes their selections. The backend returns the
    // new cart id as a RAW INTEGER (return Ok(cart.Id)), so `request`
    // IS the id — don't read request.id.
    reorder(id) {
      apiService
        .reorder({ id: id })
        .then((request) => {
          this.reorderSuccessful(request);
        })
        .catch((err) => this.reorderFailed(err));
    },
    reorderSuccessful(request) {
      localStorage.cartId = request;
      // Log the SUCCESS for the same-company dashboard Re-Order path. `request`
      // IS the new cart id. The target company is the current company (== the
      // order's company on this same-company path). The once-guard makes this a
      // no-op when reorderForOrderCompany already logged before calling here.
      this.logReorderSuccessEvent(localStorage.companyId || this.currentCompanyId, request);
      this.$router.push({
        name: "Cart",
        params: {
          id: request,
          reorder: true,
        },
      });
    },
    reorderFailed(err) {
      // Log the FAILURE for the same-company dashboard Re-Order path (best-
      // effort; once-guard no-ops when reorderForOrderCompany already logged).
      this.logReorderFailureEvent(localStorage.companyId || this.currentCompanyId, null);
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },

    // ===== COMPANY LIST LOADER (Features 1/3/4) =====
    // Same call the header makes. Stores the [{value,text}] list so
    // the current-company display helpers can resolve a name+id.
    // Failure is non-fatal — the helpers fall back to the bare id.
    getCompanies() {
      apiService
        .getCompanyList()
        .then((data) => {
          this.companies = data || [];
        })
        .catch(() => {
          this.companies = [];
        });
    },

    // Resolve the CURRENT company's full display label
    // ("Name - (CODE)") from the company select-list — the exact
    // text the header shows for the entry whose value matches
    // localStorage.companyId. Falls back to "Company <id>" when the
    // list hasn't loaded or has no match.
    currentCompanyDisplay() {
      var cid = localStorage.companyId || this.currentCompanyId || "";
      var match = (this.companies || []).find(function (c) {
        return String(c.value) === String(cid);
      });
      if (match && match.text) return match.text;
      return cid ? "Company " + cid : "your company";
    },

    // The CURRENT company's NAME ONLY — strips the trailing
    // " - (CODE)" the select-list text carries. Used for the compact
    // "Add to my company (Name)" button label.
    currentCompanyNameOnly() {
      var full = this.currentCompanyDisplay();
      // Select-list text is "Name - (CODE)"; cut at the " - (".
      var idx = full.indexOf(" - (");
      return idx > 0 ? full.substring(0, idx) : full;
    },

    // Build the "Name – ID" (or "Name – (CODE)") display string for
    // an ORDER's company straight off the order object. The order
    // carries a nested `company` { id, name, code } (see
    // OrderController list projection: it spreads o.Company). Prefer
    // the human code in parens (matches the header style); else show
    // the numeric id. Falls back gracefully when fields are missing.
    orderCompanyDisplay(o) {
      var c = (o && o.company) || {};
      var name = c.name || "";
      var idPart = c.code || (c.id != null ? c.id : "");
      if (name && idPart !== "") return name + " – " + idPart;
      if (name) return name;
      if (idPart !== "") return "Company " + idPart;
      return "this company";
    },

    // Feature 4 — toggle the per-order company detail line. $set
    // keeps Vue 2 reactivity on the keyed object.
    toggleOrderCompany(orderId) {
      var key = String(orderId);
      this.$set(
        this.expandedOrderCompany,
        key,
        !this.expandedOrderCompany[key]
      );
    },

    // ===== RE-ORDER CLICK ROUTER (Feature 1) =====
    // Decides between the unchanged same-company reorder and the
    // mismatch prompt. The order's company id is on o.company.id
    // (the list response has no top-level companyId — see
    // OrderController). Compared as strings against
    // localStorage.companyId.
    onReorderClick(o) {
      var orderCompanyId =
        o && o.company && o.company.id != null ? o.company.id : null;
      var currentCompanyId = localStorage.companyId || "";
      // Same company (or we can't determine the order's company —
      // safest is the existing, always-valid path) → unchanged flow.
      if (
        orderCompanyId == null ||
        String(orderCompanyId) === String(currentCompanyId)
      ) {
        // Stash the order + reset the log once-guards so the success/failure
        // event logging in reorderSuccessful/reorderFailed can build the full
        // breakdown for this same-company reorder.
        this.pendingReorderOrder = o;
        this.reorderSuccessLogged = false;
        this.reorderFailLogged = false;
        this.reorder(o.id);
        return;
      }
      // Different company → open the choice prompt. Clear any prior
      // failure state so the modal opens on the two-choice view.
      this.pendingReorderOrder = o;
      this.reorderMismatchBusy = false;
      this.resetReorderFailState();
      this.showReorderMismatchModal = true;
    },

    // Clears the "Add to my company" failure panel back to the initial
    // choice state. Called when the prompt (re)opens.
    resetReorderFailState() {
      this.reorderFailState = false;
      this.reorderFailReason = "";
      this.reorderMissing = [];
      this.reorderFailDiagnosing = false;
      this.reorderCanRetry = false;
      this.reorderFailLogged = false;
      this.reorderSuccessLogged = false;
    },

    // Choice 1 — "Add to [Order's Company]". The always-valid path:
    // the existing /cart/reorder/{id} backend, which clones the
    // order into a fresh cart FOR THE ORDER'S COMPANY and switches
    // the active company to it. reorderSuccessful handles the route.
    reorderForOrderCompany() {
      var o = this.pendingReorderOrder;
      if (!o) {
        this.showReorderMismatchModal = false;
        return;
      }
      this.reorderMismatchBusy = true;
      var self = this;
      // Same-company clone for the ORDER's own company — that's the target the
      // new cart lands under, so log the success against the order's company id.
      var orderCompanyId =
        o.company && o.company.id != null ? o.company.id : "";
      apiService
        .reorder({ id: o.id })
        .then(function (request) {
          self.showReorderMismatchModal = false;
          self.reorderMismatchBusy = false;
          // request IS the raw new cart id (Ok(cart.Id)). Log SUCCESS with the
          // full breakdown before routing.
          self.logReorderSuccessEvent(orderCompanyId, request);
          self.reorderSuccessful(request);
        })
        .catch(function (err) {
          self.reorderMismatchBusy = false;
          // Log the FAILURE (best-effort) against the order's company.
          self.logReorderFailureEvent(orderCompanyId, null);
          self.reorderFailed(err);
        });
    },

    // Choice 2 — "Add to my company". CROSS-COMPANY path: fetch the
    // order via reorderPreview(orderId) (ORDER repo — NOT getCart, which is a
    // CART id) and rebuild it as a NEW cart under the CURRENT company with
    // saveCart({ id:0, companyId:<current> }).
    //
    // Part-mapping caveat: a fetched order's purchaseOrders[].parts[]
    // are CartPart WRAPPERS — the real part id is p.part.id (or
    // p.part.partId), NOT the wrapper's own p.id. Quantity is
    // p.quantity. PO Numbers are cleared (number:"") so the user
    // assigns fresh POs in the Modify modal on /cart.
    //
    // On success we set localStorage.cartId but DO NOT touch
    // localStorage.companyId — the user stays in their current
    // company. On failure (parts not valid for this company's
    // machines) we show a clear danger toast and DON'T navigate.
    reorderForMyCompany() {
      var o = this.pendingReorderOrder;
      if (!o) {
        this.showReorderMismatchModal = false;
        return;
      }
      // Fresh attempt — clear any prior failure panel, then run the save.
      this.resetReorderFailState();
      this.runReorderForMyCompany();
    },

    // Re-run after the missing machine/wear parts were added to this
    // company. Same save, but keep the failure panel context (so a second
    // failure re-diagnoses against the now-updated setup).
    retryReorderForMyCompany() {
      this.reorderCanRetry = false;
      this.runReorderForMyCompany();
    },

    // Core cross-company clone: fetch the order, rebuild it as a NEW cart
    // under the CURRENT company, and save. On success route to /cart. On
    // failure, capture the REAL backend error and diagnose what's missing
    // instead of showing a generic toast.
    runReorderForMyCompany() {
      var o = this.pendingReorderOrder;
      if (!o) {
        this.showReorderMismatchModal = false;
        return;
      }
      var currentCompanyId = Number(localStorage.companyId || this.currentCompanyId) || 0;
      this.reorderMismatchBusy = true;
      var self = this;
      // Clone the order into a NEW cart UNDER THE CURRENT COMPANY via the SAME
      // proven direct-create path the same-company reorder uses (POST
      // /cart/reorder/{id}), just with ?companyId=<current>. The backend builds
      // the cart from the order's catalog line items (company-agnostic) WITHOUT
      // running the Save action's model validation — so this avoids the old
      // client-side immediate-saveCart 400 (CartModel.CompanyId [Range] /
      // blank-PO). Per-company machine-link validation happens when the user
      // saves from the Modify modal on /cart (where they also enter PO numbers),
      // exactly like the same-company flow. Returns the new cart id as a RAW
      // INTEGER (Ok(cart.Id)), so `newCartId` IS the id.
      apiService
        .reorder({ id: o.id, companyId: currentCompanyId })
        .then(function (newCartId) {
          self.reorderMismatchBusy = false;
          self.showReorderMismatchModal = false;
          // Stay in the CURRENT company — only point at the new cart.
          localStorage.cartId = newCartId;
          // Record the SUCCESS into Event Logs & History (best-effort) with the
          // full breakdown (order #, parts + per-part totals, order total, qtys)
          // BEFORE we navigate away. Target company = the CURRENT company (the
          // cart was created under it).
          self.logReorderSuccessEvent(currentCompanyId, newCartId);
          self.$router.push({
            name: "Cart",
            params: { id: newCartId, reorder: true },
          });
        })
        .catch(function (err) {
          self.reorderMismatchBusy = false;
          // The direct-create rarely fails (order-not-found / not-master). A
          // genuinely-missing machine/wear part surfaces later, at the
          // Modify-modal save on /cart, with the backend's precise message.
          self.reorderFailReason = self.extractReorderError(err);
          self.reorderFailState = true;
          self.reorderCanRetry = false;
          // RESTORE failure logging (a prior rewrite dropped this call). The
          // direct-create path has no fetched order to diagnose against, so log
          // with what we have (order #, companies, backend error) — pass null
          // for fetchedOrder. Best-effort; reorderFailLogged keeps it to once.
          self.logReorderFailureEvent(currentCompanyId, null);
        });
    },

    // Pulls the backend's actual error text out of the axios error the
    // HttpClient rejects with. Mirrors the shape the other *Failed
    // handlers read (err.error.response.data.{title,detail,traceId}), but
    // defensively handles a bare string body or a missing envelope.
    extractReorderError(err) {
      var data =
        err &&
        ((err.error && err.error.response && err.error.response.data) ||
          (err.response && err.response.data) ||
          err.data);
      if (typeof data === "string" && data.trim()) return data.trim();
      if (data && (data.title || data.detail)) {
        var msg = data.detail || data.title;
        if (data.title && data.detail && data.title !== data.detail) {
          msg = data.title + " — " + data.detail;
        }
        if (data.traceId) msg += " (TraceID: " + data.traceId + ")";
        return msg;
      }
      var fallbackMsg =
        (err && err.message) ||
        (err && err.error && err.error.message) ||
        "";
      return (
        "The order couldn't be added to your company. " +
        (fallbackMsg
          ? fallbackMsg
          : "The parts/assemblies/machines on this order aren't set up under your company.")
      );
    },

    // Diagnoses which of the order's machines / assemblies / wear parts
    // are NOT set up for the current company by fetching the company's
    // account setup (getAccountSetup) and diffing the fetched order's
    // purchaseOrders against it. Master-only data source, so for
    // non-masters we leave reorderMissing empty (they still see the real
    // error). The order's po.machineId / po.assemblyId are CATALOG ids
    // (the cart Read projection sets MachineId = the catalog Machine.Id),
    // and a fetched part's real id is p.part.id — directly comparable to
    // setup-summary item ids.
    diagnoseMissingSetup(companyId, fetchedOrder) {
      var pos = (fetchedOrder && fetchedOrder.purchaseOrders) || [];
      if (!pos.length || !this.isMasterUser) {
        // No diagnosis possible (non-master, or no POs to diff) — still log
        // the failure itself so it lands in Event Logs & History.
        this.logReorderFailureEvent(companyId, fetchedOrder);
        return;
      }
      var self = this;
      this.reorderFailDiagnosing = true;
      apiService
        .getAccountSetup(companyId)
        .then(function (setup) {
          self.reorderMissing = self.computeMissing(setup, pos);
          self.reorderFailDiagnosing = false;
          // Log after the missing list is computed so the event carries it.
          self.logReorderFailureEvent(companyId, fetchedOrder);
        })
        .catch(function () {
          // Diagnosis is best-effort — the real error is already shown.
          self.reorderFailDiagnosing = false;
          self.logReorderFailureEvent(companyId, fetchedOrder);
        });
    },

    // Best-effort: record the cross-company reorder FAILURE into Account
    // Setup → Event Logs & History (POST /account/setup-event) so the
    // failure itself is visible there, not just the later remediation/add.
    // Logs at most once per failure (reorderFailLogged guard). Captures the
    // current company, the order + its placing company, the missing item(s)
    // with suggested fix, and the real backend error. Master-gated on the
    // server, so non-masters' calls are simply ignored — we don't await or
    // surface any error.
    logReorderFailureEvent(currentCompanyId, fetchedOrder) {
      if (this.reorderFailLogged) {
        return;
      }
      this.reorderFailLogged = true;
      try {
        var o = this.pendingReorderOrder || {};
        var orderCompany = o.company || {};
        var missing = this.reorderMissing || [];
        var missingLabels = missing.map(function (m) {
          return (m.kindLabel ? m.kindLabel + ": " : "") + (m.label || "");
        });
        var suggestions = missing
          .map(function (m) {
            return m.fix || "";
          })
          .filter(function (s) {
            return !!s;
          });
        var detailParts = [
          "Cross-company reorder failed.",
          "Order #" + (o.id != null ? o.id : "?") +
            " was placed by " +
            this.orderCompanyDisplay(o) +
            ".",
          "Attempted to add it to " + this.currentCompanyDisplay() + ".",
        ];
        if (missingLabels.length) {
          detailParts.push("Missing: " + missingLabels.join("; ") + ".");
        }
        if (this.reorderFailReason) {
          detailParts.push("Backend error: " + this.reorderFailReason);
        }

        var data = {
          currentCompanyId: String(currentCompanyId || ""),
          orderId: String(o.id != null ? o.id : ""),
          orderCompanyId: String(orderCompany.id != null ? orderCompany.id : ""),
          orderCompanyName: orderCompany.name || "",
          orderCompanyCode: orderCompany.code || "",
          missingCount: String(missing.length),
        };
        if (missingLabels.length) {
          data.missingItems = missingLabels.join("; ");
        }

        apiService
          .recordSetupEvent({
            kind: "reorder-cross-company-failed",
            title: "Cross-company reorder failed",
            detail: detailParts.join(" "),
            companyId: Number(currentCompanyId) || 0,
            company: this.currentCompanyDisplay(),
            success: false,
            exception: this.reorderFailReason || "",
            suggestions: suggestions,
            data: data,
          })
          .catch(function () {
            /* best-effort: logging must never block or break the UI */
          });
      } catch (e) {
        /* swallow — diagnostics logging is non-critical */
      }
    },

    // ===== REORDER BREAKDOWN BUILDER (Issue A) =====
    // Builds a full line-item breakdown straight off the order object `o`
    // (the same `pendingReorderOrder` shape getOrders returns — confirmed in
    // OrderController.Get's projection): o.purchaseOrders[] each carry a
    // purchaseOrderNumber + parts[] = { partCode, partItem, partDescription,
    // partQuantity, partPrice, partQuantityPerMachine }. No backend call / extra
    // fetch — every field is already on `o`.
    //
    // Returns:
    //   { partCount, orderTotal, poNumbers: "PO1, PO2",
    //     partsLine: "<code> ×<qty> @ $<price> = $<line>; …",
    //     parts: [{ code, qty, price, line }] }
    // where line = qty × price (per-part total) and orderTotal prefers the
    // authoritative o.cost (the order's stored total) but falls back to the sum
    // of the line totals when o.cost is absent.
    buildReorderBreakdown(o) {
      var order = o || {};
      var pos = order.purchaseOrders || [];
      var money = function (n) {
        var v = Number(n);
        if (!isFinite(v)) v = 0;
        return v.toFixed(2);
      };
      var parts = [];
      var poNumbers = [];
      var seenPo = {};
      var lineSum = 0;
      pos.forEach(function (po) {
        var poNum = po && po.purchaseOrderNumber;
        if (poNum && !seenPo[poNum]) {
          seenPo[poNum] = true;
          poNumbers.push(poNum);
        }
        (po && po.parts ? po.parts : []).forEach(function (p) {
          var code = p.partCode || p.partItem || p.partDescription || "Part";
          var qty = Number(p.partQuantity) || 0;
          var price = Number(p.partPrice) || 0;
          var line = qty * price;
          lineSum += line;
          parts.push({ code: code, qty: qty, price: price, line: line });
        });
      });
      // Prefer the order's stored total; fall back to the summed line totals.
      var orderTotal =
        order.cost != null && isFinite(Number(order.cost))
          ? Number(order.cost)
          : lineSum;
      var partsLine = parts
        .map(function (p) {
          return (
            p.code +
            " ×" +
            p.qty +
            " @ $" +
            money(p.price) +
            " = $" +
            money(p.line)
          );
        })
        .join("; ");
      return {
        partCount: parts.length,
        orderTotal: orderTotal,
        orderTotalText: money(orderTotal),
        poNumbers: poNumbers.join(", "),
        partsLine: partsLine,
        parts: parts,
      };
    },

    // ===== REORDER SUCCESS EVENT (Issue A) =====
    // Best-effort: record a SUCCESS event into Account Setup → Event Logs &
    // History (POST /account/setup-event) whenever a reorder completes — same
    // shape/endpoint as logReorderFailureEvent, success:true. Fired from all
    // three reorder entry points (same-company dashboard reorder, the mismatch
    // prompt's "Add to order's company", and "Add to my company") + retry, so a
    // reorderSuccessLogged once-guard keeps it to one event per reorder.
    //
    // targetCompanyId — the company the new cart was created UNDER (current
    //   company for cross-company "add to my company"; the order's own company
    //   for same-company / "add to order's company").
    // newCartId — the id returned by apiService.reorder (raw integer cart id).
    //
    // Master-gated server-side, so non-Masters' calls are simply ignored; we
    // never await or surface any error.
    logReorderSuccessEvent(targetCompanyId, newCartId) {
      if (this.reorderSuccessLogged) {
        return;
      }
      this.reorderSuccessLogged = true;
      try {
        var o = this.pendingReorderOrder || {};
        var orderCompany = o.company || {};
        var b = this.buildReorderBreakdown(o);

        // Whether this was a cross-company reorder: the order's own company
        // differs from the company the cart landed under. Drives the
        // kind/title/detail wording ("Cross-company reorder" vs "Reorder").
        var orderCompanyId = orderCompany.id != null ? orderCompany.id : null;
        var crossCompany =
          orderCompanyId != null &&
          String(orderCompanyId) !== String(targetCompanyId || "");

        var orderCompanyDisplay = this.orderCompanyDisplay(o);
        // The cart landed under targetCompanyId — which is either the order's
        // own company (same-company / "add to order's company") or the current
        // company (cross-company "add to my company"). Show the matching label
        // so the detail names the company the cart was actually created under.
        var targetCompanyDisplay =
          orderCompanyId != null &&
          String(targetCompanyId || "") === String(orderCompanyId)
            ? orderCompanyDisplay
            : this.currentCompanyDisplay();

        var detail =
          "Reordered Order #" +
          (o.id != null ? o.id : "?") +
          " (placed by " +
          orderCompanyDisplay +
          ") into " +
          targetCompanyDisplay +
          ". New cart #" +
          (newCartId != null ? newCartId : "?") +
          ". " +
          b.partCount +
          (b.partCount === 1 ? " part" : " parts") +
          ", total $" +
          b.orderTotalText +
          ".";

        var data = {
          orderId: String(o.id != null ? o.id : ""),
          orderCompanyId: String(orderCompanyId != null ? orderCompanyId : ""),
          orderCompanyName: orderCompany.name || "",
          orderCompanyCode: orderCompany.code || "",
          targetCompanyId: String(targetCompanyId || ""),
          newCartId: String(newCartId != null ? newCartId : ""),
          orderTotal: b.orderTotalText,
          partCount: String(b.partCount),
          // "<code> ×<qty> @ $<price> = $<line>; …" — the full per-part
          // breakdown with line totals, quantities, and unit prices.
          parts: b.partsLine,
          poNumbers: b.poNumbers,
        };

        apiService
          .recordSetupEvent({
            kind: crossCompany
              ? "reorder-cross-company-success"
              : "reorder-success",
            title: crossCompany
              ? "Cross-company reorder succeeded"
              : "Reorder succeeded",
            detail: detail,
            companyId: Number(targetCompanyId) || 0,
            company: targetCompanyDisplay,
            success: true,
            exception: "",
            suggestions: [],
            data: data,
          })
          .catch(function () {
            /* best-effort: logging must never block or break the UI */
          });
      } catch (e) {
        /* swallow — diagnostics logging is non-critical */
      }
    },

    // Builds the missing-items list from a setup-summary payload + the
    // order's POs. Returns [{ key, kind, kindLabel, label, fix, refId,
    // partId }]. A machine miss supersedes its assembly/wear misses (you
    // add the machine first), so we don't double-list parts under a
    // machine that's entirely absent.
    computeMissing(setup, pos) {
      var idsOf = function (cat) {
        return new Set(
          ((cat && cat.items) || []).map(function (it) {
            return String(it.id);
          })
        );
      };
      var haveMachines = idsOf(setup && setup.machines);
      var haveAssemblies = new Set([
        ...idsOf(setup && setup.assemblies),
        ...idsOf(setup && setup.subAssemblies),
      ]);
      // Wear is keyed in dbo.Wear by (CompanyId, MachineId, AssemblyId, PartId),
      // so the SAME part can be a wear part under several machines/assemblies.
      // Match the order's parts on the FULL (machineId, assemblyId, partId)
      // tuple — NOT partId alone. Matching by partId alone wrongly treated a part
      // already linked as wear under ANY machine as "present", so a part the
      // order needs under a DIFFERENT machine+assembly (e.g. NDZ S 0500 DU on the
      // HAMMER MILL when the company only has it under the EXTRUDER) was never
      // flagged. setup.wearParts[].machineId / .assemblyId now carry the numeric
      // ids (added to the setup-summary projection) so the tuple is buildable.
      var tupleKey = function (mId, aId, pId) {
        return String(mId || 0) + "|" + String(aId || 0) + "|" + String(pId || 0);
      };
      var haveWearTuples = new Set(
        ((setup && setup.wearParts && setup.wearParts.items) || []).map(function (
          it
        ) {
          return tupleKey(it.machineId, it.assemblyId, it.id);
        })
      );

      var missing = [];
      var seen = {};
      var push = function (
        key,
        kind,
        kindLabel,
        label,
        fix,
        refId,
        partId,
        machineId,
        assemblyId
      ) {
        if (seen[key]) return;
        seen[key] = true;
        missing.push({
          key: key,
          kind: kind,
          kindLabel: kindLabel,
          label: label,
          fix: fix,
          refId: refId || 0,
          partId: partId || 0,
          // Carried for wear adds so the "Add" action can create the EXACT
          // dbo.Wear row (CompanyId+MachineId+AssemblyId+PartId) the order
          // needs, rather than letting the backend re-derive a (possibly wrong)
          // machine from the part's catalog assembly. 0 for non-wear rows.
          machineId: machineId || 0,
          assemblyId: assemblyId || 0,
        });
      };
      var companyName = this.currentCompanyNameOnly();

      pos.forEach(function (po) {
        var mId = po.machineId;
        var machineMissing = mId != null && !haveMachines.has(String(mId));
        if (machineMissing) {
          var mLabel = po.machine
            ? (po.machine.code ? po.machine.code + " — " : "") +
              (po.machine.description || po.machine.name || "Machine " + mId)
            : "Machine " + mId;
          push(
            "m" + mId,
            "machine",
            "Machine",
            mLabel,
            "Add machine “" + mLabel + "” to " + companyName + ".",
            mId,
            0,
            0,
            0
          );
          // Machine absent → its assemblies/parts can't be added yet; skip
          // listing them individually (adding the machine brings them in).
          return;
        }
        // Machine present — check the assembly.
        var aId = po.assemblyId;
        if (aId != null && !haveAssemblies.has(String(aId))) {
          var aLabel = po.assembly
            ? po.assembly.name || "Assembly " + aId
            : "Assembly " + aId;
          push(
            "a" + aId,
            "assembly",
            "Assembly",
            aLabel,
            "Assembly “" +
              aLabel +
              "” isn't active for " +
              companyName +
              " — an admin must enable it in the Admin area (it can't be added from here).",
            aId,
            0,
            0,
            0
          );
        }
        // Wear parts: flag any ordered part whose (machineId, assemblyId,
        // partId) tuple is NOT in this company's wear list. The machine and
        // assembly for this PO are present (checked above), so a missing tuple
        // is the genuinely-add-able wear case: the company can order on this
        // machine/assembly but has no Wear row for THIS part under it. Keyed by
        // the tuple (not partId) so the same part missing under a second
        // machine is listed separately and added to the right machine.
        var mName = po.machine
          ? (po.machine.code ? po.machine.code + " — " : "") +
            (po.machine.description || po.machine.name || "Machine " + mId)
          : "Machine " + mId;
        var aName = po.assembly
          ? po.assembly.name || "Assembly " + (po.assemblyId || "")
          : "Assembly " + (po.assemblyId || "");
        (po.parts || []).forEach(function (p) {
          var pid = (p.part && (p.part.id || p.part.partId)) || p.partId;
          if (pid == null) return;
          if (!haveWearTuples.has(tupleKey(mId, aId, pid))) {
            var pLabel =
              p.part && (p.part.code || p.part.item || p.part.description)
                ? (p.part.code || p.part.item) +
                  (p.part.description ? " — " + p.part.description : "")
                : "Part " + pid;
            push(
              // Tuple-scoped key so the same part under different machines/
              // assemblies yields distinct rows (and distinct adds).
              "w" + mId + "-" + aId + "-" + pid,
              "wear",
              "Wear part",
              pLabel + " (on " + mName + " · " + aName + ")",
              "Add wear part “" +
                pLabel +
                "” on " +
                mName +
                " to " +
                companyName +
                ".",
              pid,
              pid,
              mId,
              aId
            );
          }
        });
      });
      return missing;
    },

    // Adds the add-able missing items (machines + wear parts) to the
    // current company via setup-link-add, then offers a re-try. Each
    // successful add is recorded by the backend into the setup change
    // history, so it also appears in Account Setup → Event Logs & History.
    //
    // temporary === true  → "Add just this time": same add, but each
    //                        successful add is also recorded client-side
    //                        (localStorage via addTempSetupAdds) so it can be
    //                        auto-removed when this order is placed / quote is
    //                        finalized. The add itself is IDENTICAL to the
    //                        permanent path — the only difference is recording.
    // temporary === false → "Add for future orders & quotes": permanent.
    //                        Behavior is byte-for-byte the original.
    addMissingToMyCompany(temporary) {
      var companyId = localStorage.companyId || this.currentCompanyId;
      var items = this.addableMissing;
      if (!companyId || !items.length) return;
      var self = this;
      var cid = Number(companyId);
      // Undo entries for the items that actually succeeded (temporary path).
      var tempEntries = [];
      this.reorderMismatchBusy = true;
      var chain = Promise.resolve();
      items.forEach(function (m) {
        chain = chain.then(function () {
          var payload = {
            companyId: cid,
            type: m.kind, // "machine" | "wear"
            refId: m.refId,
            // File machine adds under "machines", wear under "wearParts"
            // so they land in the right Event Logs & History category.
            category: m.kind === "wear" ? "wearParts" : "machines",
          };
          // For wear, target the EXACT machine+assembly the order needs (the
          // tuple computeMissing flagged) so the backend inserts the precise
          // dbo.Wear row — not one re-derived from the part's catalog assembly,
          // which would land on the wrong machine when the part spans several.
          if (m.kind === "wear" && m.machineId) {
            payload.machineId = m.machineId;
            payload.assemblyId = m.assemblyId || 0;
          }
          return apiService.addSetupLink(payload).then(function () {
            // Record the undo payload only AFTER this specific add succeeded,
            // so a later failure in the chain can't strand a phantom entry for
            // an add that never happened. Mirrors the add payload exactly +
            // the item's label so cleanup can replay removeSetupLink precisely.
            if (temporary === true) {
              tempEntries.push({
                companyId: cid,
                type: payload.type,
                refId: payload.refId,
                machineId: payload.machineId || 0,
                assemblyId: payload.assemblyId || 0,
                label: m.label || "",
                category: payload.category,
              });
            }
          });
        });
      });
      chain
        .then(function () {
          self.reorderMismatchBusy = false;
          // Persist the temp-add undo entries (de-duped by composite key).
          if (temporary === true && tempEntries.length) {
            addTempSetupAdds(cid, tempEntries);
          }
          // Clear the resolved misses and present the re-try affordance.
          self.reorderMissing = [];
          self.reorderCanRetry = true;
          self.$bvToast.toast(
            "Added " +
              self.addableMissingSummaryStatic(items) +
              " to " +
              self.currentCompanyNameOnly() +
              (temporary === true
                ? " just for this re-order (auto-removed after it's placed / the quote is finalized)."
                : ".") +
              " Re-try the re-order to finish.",
            {
              title: "Setup updated",
              autoHideDelay: 8000,
              appendToast: false,
              variant: "success",
              solid: true,
            }
          );
        })
        .catch(function (err) {
          self.reorderMismatchBusy = false;
          // Persist any temp adds that DID succeed before the chain failed, so
          // those real backend rows still get auto-removed later rather than
          // being orphaned.
          if (temporary === true && tempEntries.length) {
            addTempSetupAdds(cid, tempEntries);
          }
          self.$bvToast.toast(self.extractReorderError(err), {
            title: "Couldn't add the missing items",
            autoHideDelay: 10000,
            appendToast: false,
            variant: "danger",
            solid: true,
          });
        });
    },

    // Summary string for a given items list (toast text after adding).
    addableMissingSummaryStatic(items) {
      var machines = items.filter(function (m) {
        return m.kind === "machine";
      }).length;
      var wears = items.filter(function (m) {
        return m.kind === "wear";
      }).length;
      var parts = [];
      if (machines)
        parts.push(machines + (machines > 1 ? " machines" : " machine"));
      if (wears) parts.push(wears + (wears > 1 ? " wear parts" : " wear part"));
      return parts.join(" + ") || "the missing items";
    },
    // Routes to /account/rfqdetails/{id} so the customer
    // sees the quote contents, total, and Convert/Delete
    // actions — the same RFQ Details page reached from
    // SavedCarts. The quote is NOT modified or converted
    // here; that happens only if the user explicitly hits
    // "Convert to Order" on the details page itself.
    //
    // (Previously the Review button called rfqOrder() —
    // the convert-quote-to-order endpoint — which silently
    // turned the quote into a cart and dropped the user
    // into checkout. Wrong action for a "Review" verb.)
    viewQuoteDetails(id) {
      this.$router.push({
        name: "RFQ Details",
        params: { id: id },
      });
    },
    rfqOrder(id) {
      apiService
        .rfqOrder({
          id: id,
        })
        .then((request) => {
          this.rfqOrderSuccessful(request);
        })
        .catch((err) => this.rfqOrderFailed(err));
    },
    rfqOrderSuccessful(request) {
      localStorage.cartId = request.id;
      this.$router.push({
        name: "Cart",
        params: {
          id: request.id,
          purchaseOrders: request.purchaseOrders,
          companyId: request.companyId,
          specialInstructions: request.SpecialInstructions,
        },
      });
    },
    rfqOrderFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    optionChange() {},
    getInsights() {
      apiService
        .getInsights({
          companyId: this.scopedCompanyId(),
          allTime: this.insightsPeriod === "alltime",
          accountWide: this.accountScope === "account",
        })
        .then((data) => {
          this.insights = data;
        })
        .catch(() => {
          this.insights = {
            ytdOrderCount: 0,
            ytdTotalSpend: 0,
            processedQuoteCount: 0,
            savedOrderCount: 0,
            savedQuoteCount: 0,
            orderActivityCount: 0,
            quoteActivityCount: 0,
            topParts: [],
          };
        });
    },
    switchInsightsPeriod(period) {
      this.insightsPeriod = period;
      this.getInsights();
    },
    // Flip between "whole account" and "just mine" for the insights, Completed
    // Orders, and Completed Quotes. Persists the choice and refetches all three
    // so the numbers and lists stay in lockstep.
    switchAccountScope(scope) {
      if (scope !== "account" && scope !== "mine") return;
      if (this.accountScope === scope) return;
      this.accountScope = scope;
      try {
        localStorage.setItem("jcsteele.dashboard.accountScope", scope);
      } catch (e) {
        // localStorage may be unavailable (private mode) — non-fatal.
      }
      this.getInsights();
      this.getOrders();
      this.getQuotes();
    },
    // ===== ADD-TO-CART ENTRY POINT =====
    // Replaces the old redirect-to-Search behavior. Clicking the
    // basket on a Top 5 row opens the Add-to-Cart modal in-place
    // here so the user can decide Order vs Quote, pick a PO #,
    // and set quantity without leaving Account. Confirming the
    // modal pushes to selectedParts / selectedPartsQuote and the
    // staging tray in the right column updates immediately.
    //
    // The deferred resolveTopPartContext API call from the old
    // flow is gone — that call existed to figure out which
    // machine/assembly context to drop the user into on the
    // Search page. Since we're not redirecting anymore, that
    // resolution doesn't apply. The cart-side payload assembler
    // on /orders/create / /cart will figure out the context
    // when the user clicks Continue.
    addTopPartToCart(part) {
      if (!part) return;
      // Normalize the part shape — Top 5 entries use `code`
      // while the modal expects `partCode`. Build a single
      // canonical object.
      var mapped = {
        partId: part.partId,
        partCode: part.code || part.partCode,
        description: part.description,
        // Insights signals so defaultQuantityFor can pick a
        // sensible starting qty based on the user's history.
        totalQuantity: part.totalQuantity,
        timesOrdered: part.timesOrdered,
      };
      if (!mapped.partId || !mapped.partCode) return;
      this.addToCartClick(mapped);
    },
    // ===== MODAL-OPEN GATE =====
    // Same pattern as Search.vue's addToCartClick. Detects
    // whether the part is already in either staging queue and
    // pre-fills the modal in edit mode if so; otherwise falls
    // into add mode with progressive disclosure (destination
    // toggle stays neutral when there's anything queued so the
    // user has to explicitly pick).
    addToCartClick(part) {
      if (!part || !part.partId || !part.partCode) return;
      this.pendingPart = part;

      var existing = this.findPartInQueues(part);
      if (existing) {
        this.pickerMode = "edit";
        this.pendingDestination = existing.destination;
        this.pendingPoNumber = existing.entry.poNumber || "";
        this.pendingQuantity = Number(existing.entry.quantity) || 1;
        this.useNewPo = true;
      } else {
        this.pickerMode = "add";
        // Always start in a neutral state — the user must
        // explicitly tap Order or Quote before the rest of the
        // modal reveals. Earlier this branch defaulted to
        // "order" when no staging existed (skipping the
        // question on the first add ever); the user wants the
        // toggle asked every single time so order vs quote is
        // always a deliberate choice.
        this.pendingDestination = null;
        this.useNewPo = false;
        this.pendingPoNumber = "";
        this.pendingQuantity = this.defaultQuantityFor(part);
      }

      this.showAddToCartModal = true;
    },
    // ===== MODAL DESTINATION TOGGLE =====
    onPickDestination(dest) {
      var clean = dest === "quote" ? "quote" : "order";
      this.pendingDestination = clean;
      this.pendingPoNumber = "";
      var groups = clean === "quote"
        ? this.groupedQuoteSelections
        : this.groupedSelections;
      this.useNewPo = groups.length === 0;
    },
    onPickExistingPo(poNumber) {
      this.pendingPoNumber = poNumber;
      this.useNewPo = false;
    },
    onPickNewPo() {
      this.pendingPoNumber = "";
      this.useNewPo = true;
      this.$nextTick(() => {
        var el = document.getElementById("addToCartPoInput");
        if (el) el.focus();
      });
    },
    // ===== MODAL CONFIRM =====
    // Full port of Search.vue's onAddToCartConfirm. Pushes the
    // part into the right local queue, then fires either a
    // proceedWithSelectedInPlace (no cart yet — creates one) or
    // scheduleSyncToCart (cart exists — debounced delta sync) so
    // the server-side cart actually receives the part. Without
    // this, the staging tray here showed updates but the cart
    // never received them — which is the bug the user reported.
    onAddToCartConfirm(bvModalEvent) {
      if (bvModalEvent && bvModalEvent.preventDefault) {
        bvModalEvent.preventDefault();
      }
      if (this.pickerMode !== "edit" && !this.pendingDestination) {
        return;
      }
      var trimmedPo = (this.pendingPoNumber || "").trim();
      if (!trimmedPo) {
        // Re-focus instead of closing — give the user a chance
        // to fix their input rather than silently dismissing.
        this.$nextTick(() => {
          var el = document.getElementById("addToCartPoInput");
          if (el) el.focus();
        });
        return;
      }
      var qty = Math.max(1, Math.floor(Number(this.pendingQuantity) || 1));
      var pending = this.pendingPart;
      if (!pending) {
        this.showAddToCartModal = false;
        this.resetModalState();
        return;
      }
      var dest = this.pendingDestination === "quote" ? "quote" : "order";

      if (this.pickerMode === "edit") {
        if (dest === "quote") {
          var qIdx = this.selectedPartsQuote.findIndex(function (p) {
            return p.partId === pending.partId;
          });
          if (qIdx >= 0) {
            this.$set(this.selectedPartsQuote, qIdx, Object.assign(
              {},
              this.selectedPartsQuote[qIdx],
              { poNumber: trimmedPo, quantity: qty }
            ));
          }
        } else {
          var oIdx = this.selectedParts.findIndex(function (p) {
            return p.partId === pending.partId;
          });
          if (oIdx >= 0) {
            this.$set(this.selectedParts, oIdx, Object.assign(
              {},
              this.selectedParts[oIdx],
              { poNumber: trimmedPo, quantity: qty }
            ));
          }
        }
      } else {
        if (dest === "quote") {
          this.selectedPartsQuote.push(Object.assign({}, pending, {
            poNumber: trimmedPo,
            quantity: qty,
          }));
        } else {
          this.selectedParts.push(Object.assign({}, pending, {
            poNumber: trimmedPo,
            quantity: qty,
          }));
        }
      }

      this.showAddToCartModal = false;
      this.resetModalState();

      // ===== AUTO-COMMIT TO SERVER CART =====
      // Same branching logic as Search.vue. If a server cart for
      // this destination already exists, debounce a delta sync to
      // it. If there's no cart yet, fire proceedWithSelectedInPlace
      // which creates the cart via SaveNewCart and stays on the
      // current page (no navigation — _suppressCartNav is the gate
      // that keeps the user on Account instead of jumping to /cart
      // like Search would).
      if (dest === "quote") {
        var hasQuoteCart =
          localStorage.cartIdRFQ && String(localStorage.cartIdRFQ) !== "0";
        if (!hasQuoteCart) {
          this.proceedWithSelectedInPlaceQuote();
        } else {
          this.scheduleSyncToQuoteCart();
        }
      } else {
        var hasOrderCart =
          localStorage.cartId && String(localStorage.cartId) !== "0";
        if (!hasOrderCart) {
          this.proceedWithSelectedInPlace();
        } else {
          this.scheduleSyncToCart();
        }
      }
    },
    onAddToCartCancel() {
      this.showAddToCartModal = false;
      this.resetModalState();
    },
    onAddToCartRemove() {
      var pending = this.pendingPart;
      if (!pending) {
        this.showAddToCartModal = false;
        this.resetModalState();
        return;
      }
      var dest = this.pendingDestination === "quote" ? "quote" : "order";
      this.removeQueuedPart(pending, dest);
      this.showAddToCartModal = false;
      this.resetModalState();
    },
    onAddToCartShown() {
      var el = document.getElementById("addToCartPoInput");
      if (el) el.focus();
    },
    resetModalState() {
      this.pickerMode = "add";
      this.pendingPart = null;
      this.pendingDestination = "order";
      this.pendingPoNumber = "";
      this.pendingQuantity = 1;
      this.useNewPo = false;
    },
    // ===== QUEUE HELPERS =====
    findPartInQueues(part) {
      if (!part) return null;
      var oIdx = this.selectedParts.findIndex(function (p) {
        return p.partId === part.partId;
      });
      if (oIdx >= 0) {
        return {
          destination: "order",
          index: oIdx,
          entry: this.selectedParts[oIdx],
        };
      }
      var qIdx = this.selectedPartsQuote.findIndex(function (p) {
        return p.partId === part.partId;
      });
      if (qIdx >= 0) {
        return {
          destination: "quote",
          index: qIdx,
          entry: this.selectedPartsQuote[qIdx],
        };
      }
      return null;
    },
    // Boolean helper for the Top 5 row icon swap. Wrapping
    // findPartInQueues here keeps the template readable
    // (`v-if="!isPartStaged(part)"`) and consolidates the
    // truthiness check in one place rather than scattering
    // `findPartInQueues(part) !== null` across template
    // bindings.
    isPartStaged(part) {
      return this.findPartInQueues(part) !== null;
    },
    defaultQuantityFor(part) {
      if (!part) return 1;
      var perMachine = Number(part.quantityPerMachine || part.QuantityPerMachine) || 0;
      if (perMachine > 0) return perMachine;
      var total = Number(part.totalQuantity) || 0;
      var times = Number(part.timesOrdered) || 1;
      if (total <= 0 || times <= 0) return 1;
      return Math.max(1, Math.round(total / times));
    },
    removeQueuedPart(part, destination) {
      if (!part) return;
      var dest = destination === "quote" ? "quote" : "order";
      if (dest === "quote") {
        this.selectedPartsQuote = this.selectedPartsQuote.filter(function (p) {
          return !(p.partId === part.partId && p.poNumber === part.poNumber);
        });
        // Tell the server cart about the removal — same delta-sync
        // path Search uses. scheduleSyncToQuoteCart bails cleanly
        // when there's no linked cart yet (pre-Continue queues are
        // local-only and don't need a backend round-trip).
        this.scheduleSyncToQuoteCart();
      } else {
        this.selectedParts = this.selectedParts.filter(function (p) {
          return !(p.partId === part.partId && p.poNumber === part.poNumber);
        });
        this.scheduleSyncToCart();
      }
    },
    clearSelectedParts(destination) {
      if (!destination) {
        // Clear both queues and sync both. Empty queues trigger
        // cart deletion in syncQueueToCart/Quote.
        this.selectedParts = [];
        this.selectedPartsQuote = [];
        this.scheduleSyncToCart();
        this.scheduleSyncToQuoteCart();
        return;
      }
      var dest = destination === "quote" ? "quote" : "order";
      if (dest === "quote") {
        this.selectedPartsQuote = [];
        this.scheduleSyncToQuoteCart();
      } else {
        this.selectedParts = [];
        this.scheduleSyncToCart();
      }
    },
    clearSelectionsStorage() {
      try {
        var key = this._selectionsStorageKey();
        if (key) localStorage.removeItem(key);
      } catch (e) { /* no-op */ }
    },
    clearSelectionsStorageQuote() {
      try {
        var key = this._selectionsQuoteStorageKey();
        if (key) localStorage.removeItem(key);
      } catch (e) { /* no-op */ }
    },
    // ===== CONTINUE BUTTONS =====
    // Now that onAddToCartConfirm and the row-level remove/clear
    // actions push every change through to the server cart in
    // real time (via proceedWithSelectedInPlace and
    // scheduleSyncToCart), the Continue buttons no longer need
    // to commit anything — the cart is already in sync. They're
    // pure navigation: orders go to /cart (the main cart review
    // page), quotes go to /cart/rfqsaveditems (the saved-quote-
    // items page where users finalize their RFQ). Same routes
    // Search.vue's proceedWithSelected uses for its own
    // post-save navigation.
    continueOrder() {
      if (!this.selectedParts || this.selectedParts.length === 0) return;
      this.$router.push({ name: "Cart" });
    },
    continueQuote() {
      if (!this.selectedPartsQuote || this.selectedPartsQuote.length === 0) return;
      this.$router.push("/cart/rfqsaveditems");
    },
    // ===== LOCAL STORAGE PERSISTENCE =====
    // Same key shape as Search.vue so the queues are interchangeable
    // between the two pages. When the user clicks Continue on
    // Account, /orders/create reads from this same key.
    _selectionsStorageKey(companyId) {
      var cid = companyId || this.currentCompanyId || localStorage.companyId;
      if (!cid) return null;
      return "jcsteele.insights.selections." + cid;
    },
    _selectionsQuoteStorageKey(companyId) {
      var cid = companyId || this.currentCompanyId || localStorage.companyId;
      if (!cid) return null;
      return "jcsteele.insights.selections.rfq." + cid;
    },
    saveSelectionsToStorage(forCompanyId) {
      try {
        var key = this._selectionsStorageKey(forCompanyId);
        if (!key) return;
        if (!this.selectedParts || this.selectedParts.length === 0) {
          localStorage.removeItem(key);
          return;
        }
        var payload = {
          companyId: forCompanyId || this.currentCompanyId || localStorage.companyId,
          // null cartId — Account doesn't link to a server cart
          // until the user clicks Continue and /orders/create
          // runs proceedWithSelectedInPlace. Pre-Continue queues
          // bypass loadSelectionsFromStorage's cartId-mismatch
          // check on the destination page, so they restore as
          // long as the company matches.
          cartId: null,
          committedSnapshot: null,
          selectedParts: this.selectedParts.map(function (p) {
            return {
              partId: p.partId,
              partCode: p.partCode,
              description: p.description,
              poNumber: p.poNumber,
              quantity: p.quantity,
            };
          }),
          savedAt: Date.now(),
        };
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (e) {
        console.warn("[Account/Insights] Could not persist selections", e);
      }
    },
    saveSelectionsToStorageQuote(forCompanyId) {
      try {
        var key = this._selectionsQuoteStorageKey(forCompanyId);
        if (!key) return;
        if (!this.selectedPartsQuote || this.selectedPartsQuote.length === 0) {
          localStorage.removeItem(key);
          return;
        }
        var payload = {
          companyId: forCompanyId || this.currentCompanyId || localStorage.companyId,
          cartId: null,
          committedSnapshot: null,
          selectedParts: this.selectedPartsQuote.map(function (p) {
            return {
              partId: p.partId,
              partCode: p.partCode,
              description: p.description,
              poNumber: p.poNumber,
              quantity: p.quantity,
            };
          }),
          savedAt: Date.now(),
        };
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (e) {
        console.warn("[Account/Insights/Quote] Could not persist selections", e);
      }
    },
    loadSelectionsFromStorage() {
      try {
        var key = this._selectionsStorageKey();
        if (!key) return;
        var raw = localStorage.getItem(key);
        if (!raw) {
          this.selectedParts = [];
          return;
        }
        var saved = JSON.parse(raw);
        if (!saved || !Array.isArray(saved.selectedParts)) {
          this.selectedParts = [];
          return;
        }
        // Skip the cartId-mismatch check that Search.vue does —
        // here we just want whatever's saved, since this page
        // doesn't bind to a specific cart. If the user had moved
        // on to a placed order, /orders/create will handle the
        // mismatch on its own next mount.
        this.selectedParts = saved.selectedParts;
      } catch (e) {
        console.warn("[Account/Insights] Could not restore selections", e);
        this.selectedParts = [];
      }
    },
    loadSelectionsFromStorageQuote() {
      try {
        var key = this._selectionsQuoteStorageKey();
        if (!key) return;
        var raw = localStorage.getItem(key);
        if (!raw) {
          this.selectedPartsQuote = [];
          return;
        }
        var saved = JSON.parse(raw);
        if (!saved || !Array.isArray(saved.selectedParts)) {
          this.selectedPartsQuote = [];
          return;
        }
        this.selectedPartsQuote = saved.selectedParts;
      } catch (e) {
        console.warn("[Account/Insights/Quote] Could not restore selections", e);
        this.selectedPartsQuote = [];
      }
    },

    // ===== CART SYNC ON MOUNT =====
    // Mirrors Search.vue's syncFromActiveCart. The active cart's
    // server-side contents are the source of truth — what the user
    // expects to see in the staging tray. When localStorage.cartId
    // points to a real cart, fetch it and rebuild selectedParts
    // from its purchaseOrders/parts. When there is no active cart,
    // CLEAR the local queue (and its localStorage tag) instead of
    // leaving stale state behind.
    //
    // The clear-on-no-cart behavior is the key difference from
    // Search.vue, which lets pre-Continue queues persist between
    // visits as a feature. On Account, the staging tray is
    // intended as a faithful preview of the cart — not a
    // separately-mutable scratch surface — so anything in the
    // queue that doesn't correspond to an active cart is treated
    // as stale and dropped. That fixes the case where prior
    // Search-page testing left staging entries in localStorage
    // that would otherwise show up here even with no active cart.
    async syncFromActiveCart() {
      if (!this.currentCompanyId && !localStorage.companyId) return;

      // No active order cart → clear any stale order queue so the
      // staging tray's order section disappears (and the column
      // hides if the quote section is also empty).
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        if (this.selectedParts && this.selectedParts.length > 0) {
          this.selectedParts = [];
        }
        // Also drop the localStorage payload itself so a refresh
        // doesn't re-hydrate it next mount.
        try {
          var orderKey = this._selectionsStorageKey();
          if (orderKey) localStorage.removeItem(orderKey);
        } catch (e) { /* no-op on storage errors */ }
        return;
      }

      try {
        var cart = await apiService.getCart({ id: localStorage.cartId });
        if (!cart) return;

        // Filter: skip quote carts. localStorage.cartId should
        // only ever point to an Order cart, but defensively
        // bail if the discriminator says otherwise.
        var isQuote =
          cart.discriminator === "Rfq" ||
          cart.Discriminator === "Rfq" ||
          cart.discriminator === 1 ||
          cart.Discriminator === 1;
        if (isQuote) return;

        var pos = cart.purchaseOrders || [];
        if (pos.length === 0) {
          // Cart exists but has zero parts (e.g. user cleared
          // it from /cart) — clear local queue too so the
          // staging tray reflects reality.
          this.selectedParts = [];
          try {
            var k = this._selectionsStorageKey();
            if (k) localStorage.removeItem(k);
          } catch (e) { /* no-op */ }
          return;
        }

        // Convert cart shape → selectedParts shape. Same projection
        // as Search.vue's syncFromActiveCart so the rendered tray
        // is consistent across pages.
        var derived = [];
        pos.forEach(function (po) {
          (po.parts || []).forEach(function (p) {
            var partInfo = p.part || {};
            derived.push({
              partId: p.id,
              partCode: partInfo.code || "",
              description: partInfo.description || "",
              poNumber: po.number || "",
              quantity: p.quantity || 1,
            });
          });
        });

        this.selectedParts = derived;
      } catch (e) {
        console.warn(
          "[Account] cart-derivation on mount failed:",
          e
        );
      }
    },
    async syncFromActiveQuoteCart() {
      if (!this.currentCompanyId && !localStorage.companyId) return;

      if (!localStorage.cartIdRFQ || String(localStorage.cartIdRFQ) === "0") {
        if (this.selectedPartsQuote && this.selectedPartsQuote.length > 0) {
          this.selectedPartsQuote = [];
        }
        try {
          var quoteKey = this._selectionsQuoteStorageKey();
          if (quoteKey) localStorage.removeItem(quoteKey);
        } catch (e) { /* no-op */ }
        return;
      }

      try {
        var cart = await apiService.getCart({ id: localStorage.cartIdRFQ });
        if (!cart) return;

        var isQuote =
          cart.discriminator === "Rfq" ||
          cart.Discriminator === "Rfq" ||
          cart.discriminator === 1 ||
          cart.Discriminator === 1;
        if (!isQuote) return;

        var pos = cart.purchaseOrders || [];
        if (pos.length === 0) {
          this.selectedPartsQuote = [];
          try {
            var k = this._selectionsQuoteStorageKey();
            if (k) localStorage.removeItem(k);
          } catch (e) { /* no-op */ }
          return;
        }

        var derived = [];
        pos.forEach(function (po) {
          (po.parts || []).forEach(function (p) {
            var partInfo = p.part || {};
            derived.push({
              partId: p.id,
              partCode: partInfo.code || "",
              description: partInfo.description || "",
              poNumber: po.number || "",
              quantity: p.quantity || 1,
            });
          });
        });

        this.selectedPartsQuote = derived;
      } catch (e) {
        console.warn(
          "[Account/Quote] cart-derivation on mount failed:",
          e
        );
      }
    },

    // =================================================================
    // ===== CART COMMIT — full port from Search.vue =====
    // =================================================================
    // Everything below is a direct port of the cart-write side of
    // Search.vue. Keeping the implementation byte-similar (rather
    // than refactoring into a mixin) so any bug fixes that go into
    // Search are easy to mirror here, and so the two pages handle
    // the same edge cases identically.
    //
    // Top-level entry points:
    //   onAddToCartConfirm  → calls proceedWithSelectedInPlace*
    //                          (no cart) or scheduleSyncToCart*
    //                          (cart exists)
    //   removeQueuedPart    → scheduleSyncToCart* (delta sync)
    //   clearSelectedParts  → scheduleSyncToCart* (cart deletion)
    //
    // Both proceed* and sync* converge on apiService.saveCart with
    // the same payload shape. The only difference is whether the
    // user is creating a brand-new cart (id=0 → SaveNewCart) or
    // adding to an existing one (id=cartId → SaveOldCart). The
    // backend dispatches based on the id field.
    //
    // _suppressCartNav is the in-place flag — Search uses it to
    // distinguish "user clicked Continue with N parts at the
    // bottom of the page" (navigates to /cart) from "user added
    // a single part via the Add-to-Cart modal" (stays on the
    // current page). Account is always in-place since there's
    // no Continue-to-Cart navigation step on this page.

    buildCommittedSnapshot() {
      return (this.selectedParts || []).map(function (p) {
        return {
          partId: p.partId,
          poNumber: p.poNumber,
          quantity: p.quantity,
        };
      });
    },
    buildCommittedSnapshotQuote() {
      return (this.selectedPartsQuote || []).map(function (p) {
        return {
          partId: p.partId,
          poNumber: p.poNumber,
          quantity: p.quantity,
        };
      });
    },

    // ===== PART CONTEXT RESOLUTION =====
    // Looks up the CompanyMachine ID and Assembly ID for a part —
    // both required by the backend's CreatePurchaseOrder. Tries
    // PartController.GetPartContext first (cleanest), falls back
    // to URL parsing on resolveTopPartContext if that endpoint
    // isn't deployed.
    async resolvePartContext(p) {
      var enriched = Object.assign({}, p, {
        _companyMachineId: null,
        _assemblyId: null,
        _resolveDiagnostics: [],
      });
      var companyId = this.currentCompanyId || localStorage.companyId;

      // Path 1 (preferred): apiService.getPartContext
      var hasGetPartContext = typeof apiService.getPartContext === "function";
      if (!hasGetPartContext) {
        enriched._resolveDiagnostics.push(
          "apiService.getPartContext is not defined — add the wrapper to FeApiService.js"
        );
      } else {
        try {
          var ctx = await apiService.getPartContext({
            partId: p.partId,
            companyId: companyId,
          });
          if (ctx) {
            enriched._companyMachineId =
              ctx.companyMachineId || ctx.CompanyMachineId || null;
            enriched._assemblyId = ctx.assemblyId || ctx.AssemblyId || null;
            if (enriched._companyMachineId && enriched._assemblyId) {
              enriched._resolveDiagnostics.push(
                "getPartContext OK: companyMachineId=" +
                  enriched._companyMachineId +
                  ", assemblyId=" +
                  enriched._assemblyId
              );
              return enriched;
            }
            enriched._resolveDiagnostics.push(
              "getPartContext returned but missing IDs: " +
                JSON.stringify(ctx)
            );
          } else {
            enriched._resolveDiagnostics.push(
              "getPartContext returned empty/null"
            );
          }
        } catch (e) {
          var status = e && e.error && e.error.response && e.error.response.status;
          enriched._resolveDiagnostics.push(
            "getPartContext threw" +
              (status ? " (HTTP " + status + ")" : "") +
              " — likely the PartController.GetPartContext endpoint isn't deployed yet"
          );
          console.warn(
            "[Account/Insights] getPartContext failed for " + p.partCode,
            e
          );
        }
      }

      // Path 2 (fallback): URL parsing on resolveTopPartContext.
      try {
        var result = await apiService.resolveTopPartContext({
          partId: p.partId,
          partCode: p.partCode,
          companyId: companyId,
        });
        if (result && result.redirectUrl) {
          enriched._resolveDiagnostics.push(
            "resolveTopPartContext URL: " + result.redirectUrl
          );
          enriched._companyMachineId = this.parseUrlId(result.redirectUrl, [
            "companyMachineId",
            "machineId",
            "machineid",
          ]);
          enriched._assemblyId = this.parseUrlId(result.redirectUrl, [
            "assemblyId",
            "assemblyid",
          ]);
          if (!enriched._companyMachineId || !enriched._assemblyId) {
            enriched._resolveDiagnostics.push(
              "URL parsing produced: companyMachineId=" +
                enriched._companyMachineId +
                ", assemblyId=" +
                enriched._assemblyId +
                " — URL doesn't carry the IDs we need"
            );
          }
        }
      } catch (e) {
        enriched._resolveDiagnostics.push(
          "resolveTopPartContext threw — see console"
        );
        console.warn(
          "[Account/Insights] resolveTopPartContext failed for " + p.partCode,
          e
        );
      }

      console.log(
        "[Account/Insights] resolved " + p.partCode + ":",
        enriched._resolveDiagnostics
      );
      return enriched;
    },
    parseUrlId(url, paramNames) {
      if (!url || !paramNames || !paramNames.length) return null;
      try {
        var parsed = new URL(url, window.location.origin);
        for (var i = 0; i < paramNames.length; i++) {
          var v = parsed.searchParams.get(paramNames[i]);
          if (v) return v;
        }
        return null;
      } catch (e) {
        return null;
      }
    },

    // ===== ORDER CART COMMIT =====
    async proceedWithSelectedInPlace() {
      this._suppressCartNav = true;
      try {
        await this.proceedWithSelected();
      } finally {
        this._suppressCartNav = false;
      }
    },
    async proceedWithSelected() {
      if (this.addingInsightPart || this.selectedParts.length === 0) return;
      var companyId = this.currentCompanyId || localStorage.companyId;
      if (!companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        var resolved = await Promise.all(
          this.selectedParts.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          var codes = missing.map(function (p) { return p.partCode; }).join(", ");
          this.$bvToast.toast(
            "Could not resolve the machine and assembly context for: " +
              codes +
              ". The backend needs a getPartContext endpoint — see the " +
              "console log for the payload we tried to send.",
            {
              title: "Cart update blocked",
              autoHideDelay: 12000,
              variant: "warning",
              solid: true,
            }
          );
          console.warn(
            "[Account/Insights → saveCart] could not resolve context for parts:",
            missing
          );
          return;
        }

        var existingCartId = parseInt(localStorage.cartId, 10) || 0;
        var snapshotKeys = {};
        (this.committedSnapshot || []).forEach(function (s) {
          snapshotKeys[s.partId + "|" + (s.poNumber || "")] = true;
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = existingCartId > 0
          && (this.committedSnapshot || []).some(function (s) {
              var key = s.partId + "|" + (s.poNumber || "");
              return !currentKeys[key];
            });

        if (addedParts.length === 0 && !hasRemovals) {
          this.committedSnapshot = this.buildCommittedSnapshot();
          this.saveSelectionsToStorage();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        var partsForSave;
        if (hasRemovals || existingCartId === 0) {
          partsForSave = resolved;
        } else {
          partsForSave = addedParts;
        }

        var poGroups = {};
        var poOrder = [];
        partsForSave.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        if (hasRemovals && existingCartId > 0) {
          try {
            await apiService.deleteCart({ id: existingCartId });
          } catch (e) {
            console.warn(
              "[Account/Insights → saveCart] deleteCart before recreate failed:",
              e
            );
          }
        }

        var saveCartIdForPayload = hasRemovals ? 0 : existingCartId;
        var savePayload = {
          id: saveCartIdForPayload,
          purchaseOrders: purchaseOrders,
          companyId: Number(companyId) || 0,
          specialInstructions: "",
          CreateQuote: false,
        };

        console.log("[Account/Insights → saveCart] sending payload:", savePayload);
        var response = await apiService.saveCart(savePayload);

        if (response && response.id) {
          localStorage.cartId = response.id;
        }

        this.committedSnapshot = this.buildCommittedSnapshot();
        this.saveSelectionsToStorage();

        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }

        // No navigation on Account — _suppressCartNav is always
        // set by proceedWithSelectedInPlace. Search would push to
        // /cart here for the Continue-with-N-parts case, but on
        // Account every commit is in-place.
      } catch (err) {
        var details = "";
        try {
          var resp = err && err.error && err.error.response;
          if (resp && resp.data) {
            console.error("[Account/Insights → saveCart] backend response:", resp.data);
            details =
              resp.data.title ||
              resp.data.exception ||
              (resp.data.errors && JSON.stringify(resp.data.errors)) ||
              JSON.stringify(resp.data);
          } else {
            console.error("[Account/Insights → saveCart] error:", err);
            details = (err && err.message) || String(err);
          }
        } catch (e) {
          console.error("[Account/Insights → saveCart] error (unparseable):", err);
        }

        this.$bvToast.toast(
          "Could not add the selected parts to your cart. " +
            (details
              ? "Server said: " + details
              : "Check the browser console for details."),
          {
            title: "Cart update failed",
            autoHideDelay: 9000,
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
      }
    },
    scheduleSyncToCart() {
      // Pre-Continue queues (no linked cart) get the
      // proceedWithSelectedInPlace path instead of the debounced
      // delta sync. This branch only fires when there's an
      // existing cart to mirror.
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        return;
      }
      if (this._syncTimer) {
        clearTimeout(this._syncTimer);
      }
      var self = this;
      this._syncTimer = setTimeout(function () {
        self._syncTimer = null;
        self.syncQueueToCart();
      }, 300);
    },
    async syncQueueToCart() {
      if (this.addingInsightPart) {
        this._syncDirty = true;
        return;
      }
      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        return;
      }
      var companyId = this.currentCompanyId || localStorage.companyId;
      if (!companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        // Empty queue → delete cart (user × 'd everything out)
        if (!this.selectedParts || this.selectedParts.length === 0) {
          var cartIdToDelete = parseInt(localStorage.cartId, 10);
          try {
            await apiService.deleteCart({ id: cartIdToDelete });
          } catch (e) {
            console.warn(
              "[Account/Insights → sync] deleteCart returned error:",
              e
            );
          }
          localStorage.cartId = 0;
          this.clearSelectionsStorage();
          this.committedSnapshot = null;
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        var resolved = await Promise.all(
          this.selectedParts.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          console.warn(
            "[Account/Insights → sync] skipped — some parts failed to resolve:",
            missing.map(function (m) { return m.partCode; }).join(", ")
          );
          return;
        }

        // Add / remove / qty-change delta
        var snapshotKeys = {};
        var snapshotQtys = {};
        (this.committedSnapshot || []).forEach(function (s) {
          var k = s.partId + "|" + (s.poNumber || "");
          snapshotKeys[k] = true;
          snapshotQtys[k] = Number(s.quantity);
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = (this.committedSnapshot || []).some(function (s) {
          var key = s.partId + "|" + (s.poNumber || "");
          return !currentKeys[key];
        });
        var hasQtyChange = resolved.some(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          if (!(key in snapshotQtys)) return false;
          return Number(p.quantity) !== snapshotQtys[key];
        });

        if (addedParts.length === 0 && !hasRemovals && !hasQtyChange) {
          this.committedSnapshot = this.buildCommittedSnapshot();
          this.saveSelectionsToStorage();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        var needsRecreate = hasRemovals || hasQtyChange;
        var partsForPayload = needsRecreate ? resolved : addedParts;

        var poGroups = {};
        var poOrder = [];
        partsForPayload.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        if (needsRecreate) {
          var oldCartId = parseInt(localStorage.cartId, 10) || 0;
          if (oldCartId > 0) {
            try {
              await apiService.deleteCart({ id: oldCartId });
            } catch (e) {
              console.warn(
                "[Account/Insights → sync] deleteCart before recreate failed:",
                e
              );
            }
          }
        }

        var saveCartId = needsRecreate
          ? 0
          : parseInt(localStorage.cartId, 10) || 0;
        var savePayload = {
          id: saveCartId,
          purchaseOrders: purchaseOrders,
          companyId: Number(companyId) || 0,
          specialInstructions: "",
          CreateQuote: false,
        };

        var response = await apiService.saveCart(savePayload);
        if (response && response.id) {
          localStorage.cartId = response.id;
        }

        this.committedSnapshot = this.buildCommittedSnapshot();
        this.saveSelectionsToStorage();

        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
      } catch (err) {
        console.warn("[Account/Insights → sync] saveCart failed:", err);
        this.$bvToast.toast(
          "Couldn't sync the change to your cart. The staging tray " +
            "is up to date — refresh the page to retry.",
          {
            title: "Cart sync failed",
            autoHideDelay: 6000,
            variant: "warning",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
        if (this._syncDirty) {
          this._syncDirty = false;
          this.scheduleSyncToCart();
        }
      }
    },

    // ===== QUOTE CART COMMIT =====
    // Mirror of the order-cart methods above, but with
    // CreateQuote: true and localStorage.cartIdRFQ instead of
    // localStorage.cartId. Same delta logic, same payload shape.
    async proceedWithSelectedInPlaceQuote() {
      this._suppressCartNav = true;
      try {
        await this.proceedWithSelectedQuote();
      } finally {
        this._suppressCartNav = false;
      }
    },
    async proceedWithSelectedQuote() {
      if (this.addingInsightPart || this.selectedPartsQuote.length === 0) return;
      var companyId = this.currentCompanyId || localStorage.companyId;
      if (!companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        var resolved = await Promise.all(
          this.selectedPartsQuote.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          var codes = missing.map(function (p) { return p.partCode; }).join(", ");
          this.$bvToast.toast(
            "Could not resolve the machine and assembly context for: " +
              codes +
              ". The backend needs a getPartContext endpoint — see the " +
              "console log for the payload we tried to send.",
            {
              title: "Quote update blocked",
              autoHideDelay: 12000,
              variant: "warning",
              solid: true,
            }
          );
          console.warn(
            "[Account/Insights/Quote → saveCart] could not resolve context for parts:",
            missing
          );
          return;
        }

        var existingCartId = parseInt(localStorage.cartIdRFQ, 10) || 0;
        var snapshotKeys = {};
        (this.committedSnapshotQuote || []).forEach(function (s) {
          snapshotKeys[s.partId + "|" + (s.poNumber || "")] = true;
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = existingCartId > 0
          && (this.committedSnapshotQuote || []).some(function (s) {
              var key = s.partId + "|" + (s.poNumber || "");
              return !currentKeys[key];
            });

        if (addedParts.length === 0 && !hasRemovals) {
          this.committedSnapshotQuote = this.buildCommittedSnapshotQuote();
          this.saveSelectionsToStorageQuote();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        var partsForSave;
        if (hasRemovals || existingCartId === 0) {
          partsForSave = resolved;
        } else {
          partsForSave = addedParts;
        }

        var poGroups = {};
        var poOrder = [];
        partsForSave.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        if (hasRemovals && existingCartId > 0) {
          try {
            await apiService.deleteCart({ id: existingCartId });
          } catch (e) {
            console.warn(
              "[Account/Insights/Quote → saveCart] deleteCart before recreate failed:",
              e
            );
          }
        }

        var saveCartIdForPayload = hasRemovals ? 0 : existingCartId;
        var savePayload = {
          id: saveCartIdForPayload,
          purchaseOrders: purchaseOrders,
          companyId: Number(companyId) || 0,
          specialInstructions: "",
          CreateQuote: true,
        };

        console.log("[Account/Insights/Quote → saveCart] sending payload:", savePayload);
        var response = await apiService.saveCart(savePayload);

        if (response && response.id) {
          localStorage.cartIdRFQ = response.id;
        }

        this.committedSnapshotQuote = this.buildCommittedSnapshotQuote();
        this.saveSelectionsToStorageQuote();

        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
      } catch (err) {
        var details = "";
        try {
          var resp = err && err.error && err.error.response;
          if (resp && resp.data) {
            console.error("[Account/Insights/Quote → saveCart] backend response:", resp.data);
            details =
              resp.data.title ||
              resp.data.exception ||
              (resp.data.errors && JSON.stringify(resp.data.errors)) ||
              JSON.stringify(resp.data);
          } else {
            console.error("[Account/Insights/Quote → saveCart] error:", err);
            details = (err && err.message) || String(err);
          }
        } catch (e) {
          console.error("[Account/Insights/Quote → saveCart] error (unparseable):", err);
        }

        this.$bvToast.toast(
          "Could not add the selected parts to your quote. " +
            (details
              ? "Server said: " + details
              : "Check the browser console for details."),
          {
            title: "Quote update failed",
            autoHideDelay: 9000,
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
      }
    },
    scheduleSyncToQuoteCart() {
      if (!localStorage.cartIdRFQ || String(localStorage.cartIdRFQ) === "0") {
        return;
      }
      if (this._syncTimerQuote) {
        clearTimeout(this._syncTimerQuote);
      }
      var self = this;
      this._syncTimerQuote = setTimeout(function () {
        self._syncTimerQuote = null;
        self.syncQueueToQuoteCart();
      }, 300);
    },
    async syncQueueToQuoteCart() {
      if (this.addingInsightPart) {
        this._syncDirtyQuote = true;
        return;
      }
      if (!localStorage.cartIdRFQ || String(localStorage.cartIdRFQ) === "0") {
        return;
      }
      var companyId = this.currentCompanyId || localStorage.companyId;
      if (!companyId) return;

      this.addingInsightPart = true;
      var self = this;

      try {
        if (!this.selectedPartsQuote || this.selectedPartsQuote.length === 0) {
          var cartIdToDelete = parseInt(localStorage.cartIdRFQ, 10);
          try {
            await apiService.deleteCart({ id: cartIdToDelete });
          } catch (e) {
            console.warn(
              "[Account/Insights/Quote → sync] deleteCart returned error:",
              e
            );
          }
          localStorage.cartIdRFQ = 0;
          this.clearSelectionsStorageQuote();
          this.committedSnapshotQuote = null;
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        var resolved = await Promise.all(
          this.selectedPartsQuote.map(async function (p) {
            return await self.resolvePartContext(p);
          })
        );

        var missing = resolved.filter(function (p) {
          return !p._companyMachineId || !p._assemblyId;
        });
        if (missing.length > 0) {
          console.warn(
            "[Account/Insights/Quote → sync] skipped — some parts failed to resolve:",
            missing.map(function (m) { return m.partCode; }).join(", ")
          );
          return;
        }

        var snapshotKeys = {};
        var snapshotQtys = {};
        (this.committedSnapshotQuote || []).forEach(function (s) {
          var k = s.partId + "|" + (s.poNumber || "");
          snapshotKeys[k] = true;
          snapshotQtys[k] = Number(s.quantity);
        });
        var currentKeys = {};
        resolved.forEach(function (p) {
          currentKeys[p.partId + "|" + (p.poNumber || "")] = true;
        });

        var addedParts = resolved.filter(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          return !snapshotKeys[key];
        });
        var hasRemovals = (this.committedSnapshotQuote || []).some(function (s) {
          var key = s.partId + "|" + (s.poNumber || "");
          return !currentKeys[key];
        });
        var hasQtyChange = resolved.some(function (p) {
          var key = p.partId + "|" + (p.poNumber || "");
          if (!(key in snapshotQtys)) return false;
          return Number(p.quantity) !== snapshotQtys[key];
        });

        if (addedParts.length === 0 && !hasRemovals && !hasQtyChange) {
          this.committedSnapshotQuote = this.buildCommittedSnapshotQuote();
          this.saveSelectionsToStorageQuote();
          if (this.$cartBus) {
            this.$cartBus.$emit("cart-updated");
          }
          return;
        }

        var needsRecreate = hasRemovals || hasQtyChange;
        var partsForPayload = needsRecreate ? resolved : addedParts;

        var poGroups = {};
        var poOrder = [];
        partsForPayload.forEach(function (p) {
          if (!poGroups[p.poNumber]) {
            poGroups[p.poNumber] = [];
            poOrder.push(p.poNumber);
          }
          poGroups[p.poNumber].push(p);
        });

        var purchaseOrders = poOrder.map(function (po) {
          var parts = poGroups[po];
          var first = parts[0];
          return {
            number: po,
            assemblyId: Number(first._assemblyId) || 0,
            machineId: Number(first._companyMachineId) || 0,
            configuredWear: true,
            parts: parts.map(function (p) {
              return {
                id: Number(p.partId) || 0,
                quantity: Number(p.quantity) || 1,
                quantityPerMachine: 0,
              };
            }),
          };
        });

        if (needsRecreate) {
          var oldCartId = parseInt(localStorage.cartIdRFQ, 10) || 0;
          if (oldCartId > 0) {
            try {
              await apiService.deleteCart({ id: oldCartId });
            } catch (e) {
              console.warn(
                "[Account/Insights/Quote → sync] deleteCart before recreate failed:",
                e
              );
            }
          }
        }

        var saveCartId = needsRecreate
          ? 0
          : parseInt(localStorage.cartIdRFQ, 10) || 0;
        var savePayload = {
          id: saveCartId,
          purchaseOrders: purchaseOrders,
          companyId: Number(companyId) || 0,
          specialInstructions: "",
          CreateQuote: true,
        };

        var response = await apiService.saveCart(savePayload);
        if (response && response.id) {
          localStorage.cartIdRFQ = response.id;
        }

        this.committedSnapshotQuote = this.buildCommittedSnapshotQuote();
        this.saveSelectionsToStorageQuote();

        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
      } catch (err) {
        console.warn("[Account/Insights/Quote → sync] saveCart failed:", err);
        this.$bvToast.toast(
          "Couldn't sync the change to your quote. The staging tray " +
            "is up to date — refresh the page to retry.",
          {
            title: "Quote sync failed",
            autoHideDelay: 6000,
            variant: "warning",
            solid: true,
          }
        );
      } finally {
        this.addingInsightPart = false;
        if (this._syncDirtyQuote) {
          this._syncDirtyQuote = false;
          this.scheduleSyncToQuoteCart();
        }
      }
    },
    scrollTo() {
      $("html, body").animate(
        {
          scrollTop: $("#acctQuotes").offset().top,
        },
        500
      );
    },
  },
};
</script>

<style scoped>
/* Admin welcome / "select a company" empty-state styles moved into the
   reusable SelectCompanyPrompt.vue component (src/components/Common). The
   #adminWelcome wrapper in this template now only centers that component
   via its .container. */

.account-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 20px;
  /* Reduced bottom margin (was 18px → 8px) so the welcome
     heading and "Show Important Information" button sit closer
     to the insights panel below. The earlier spacing had the
     welcome floating in too much whitespace; tightening it
     pulls the page content up so the dashboard reads as a
     denser, more intentional layout. */
  margin: 0 30px 8px 30px;
}

.welcome-wrapper {
  flex: 1 1 auto;
  min-width: 0;
}

.dashboard-welcome {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0;
  /* Block display (default for h1) lets the inline <br> in the
     template actually break the line — the previous flex layout
     was treating <br> as a zero-size flex item, which collapsed
     the break and put both lines on the same row. */
}

.welcome-subtext {
  /* `display: block` on this inline span guarantees the subtext
     drops onto its own line even if the <br> is ever stripped
     during minification. Combined with the h1 going back to
     block, "Here are some insights just for you!" sits below
     "Welcome back, Michael!" as a separate line. */
  display: block;
  margin-top: 4px;
  font-weight: 500;
  color: #666;
  font-size: 16px;
}

.important-info-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #c0392b;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  border-bottom: 2px solid #c0392b;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  margin-left: auto;
}

.important-info-toggle:hover {
  color: #a93226;
  border-color: #a93226;
}

.important-icon {
  font-size: 16px;
  line-height: 1;
}

.dashboard-section-title {
  text-align: center;
  color: #c0392b;
  font-weight: 700;
  margin-bottom: 14px;
}

.dashboard-section-title-sm {
  margin-bottom: 0;
}

#acctInsights {
  /* Tagline above (.dashboard-welcome-subtext-line) now carries a
     0 top margin, so the only spacing between the welcome heading and
     the insights content is this section's small negative pull-up plus
     the heading's own 6px bottom margin. With the Important Information
     panel gone from this page (now the floating ImportantInfoWidget),
     the dashboard content is pulled up to sit right beneath the
     "Welcome back" heading per the user's request. */
  padding: 0 0 30px 0;
  /* #acctInsights is now the first content section (it hosts the welcome
     heading too). margin-top:0 — a negative pull-up dragged the welcome
     heading up UNDER the fixed breadcrumb bar, which clipped its top
     (user-reported "Welcome Back is cut off"). The body's padding-top
     already clears the header + breadcrumb, so 0 keeps the heading fully
     visible right below them. */
  margin-top: 0;
}

#acctInsights h3 {
  font-size: 18px;
  margin-top: 10px;
}

.insights-card-row {
  margin-left: 0;
  margin-right: 0;
}

.insights-inner-block {
  margin-left: 0;
  margin-right: 0;
  margin-top: 24px;
}

#acctInsights .mt-3 {
  margin-left: 0;
  margin-right: 0;
}

/* ===== INSIGHTS CONTAINER =====
   Single panel wrapping all four insight stats (Total Spend
   banner, Orders, Quotes, Saved Carts). Carries the white-bg
   + red-top + shadow signature shared with .dashboard-card
   (Top 5, Completed Orders/Quotes) and .staging-tray-wrapper
   so the dashboard reads as one cohesive grid of red-accented
   panels. Inner content (.insight-card children) is now
   chromeless — just centered text blocks — so the OUTER
   container is the one and only card boundary on this group. */
/* ===== ACCOUNT HEADER + IMPORTANT INFO STACK =====
   #acctImportantInfo wraps three sequenced sections:
   "Welcome back, X!" headline → Important Information card →
   "Here are some insights just for you!" tagline.

   The three together visually open the page; the dashboard
   content (#acctInsights) follows below. */
#acctImportantInfo {
  /* Pull the entire block UP with a negative top margin so
     the welcome heading hugs the breadcrumb bar above. The
     parent layout adds its own top padding here we can't
     touch from inside this scoped style block; the negative
     margin compensates without restructuring the page-level
     wrapper. User feedback over multiple iterations was
     that the gap above "Welcome Back" was too tall — this
     pulls it as close to the breadcrumb as we can get
     without overlap. */
  margin-top: -16px;
  padding: 0 0 4px 0;
}

.dashboard-welcome-top {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  /* Tightened bottom margin so the welcome heading sits
     close to the Important Information card below it. */
  margin: 0 30px 6px 30px;
}

/* "Here are some insights just for you!" — section heading
   for the dashboard cards below. Centered (visually) in the
   gap between the bottom of the Important Information block
   above and the top of the Insights container below by
   using equal top + bottom margins. The user asked for it
   to read as a balanced separator between the two sections
   rather than hugging either one. Margins of 14px each side
   land the line on the visual midpoint of that gap. */
.dashboard-welcome-subtext-line {
  /* Top margin 0 so the tagline (and the content it introduces) hugs
     the "Welcome back" heading above — see #acctInsights margin-top.
     Bottom margin keeps it separated from the stat cards below. */
  margin: 0 30px 12px 30px;
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

/* The shadowed container that holds all the system
   messages. Mirrors the .insights-container / .dashboard-card
   / .staging-tray-wrapper unified card signature so it sits
   in the same family. */
.important-info-container {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  padding: 14px 16px 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  margin: 0 30px;
}

.important-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #555;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e6e6e6;
}

.important-info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #c0392b;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding-bottom: 1px;
}

/* The two messages stack inside the container — no per-
   message card chrome. Each row gets a thin top divider
   except the first, so the eye reads them as a unified
   list rather than separate boxes. */
.important-info-list {
  display: flex;
  flex-direction: column;
}

.important-info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #f4f4f4;
}
.important-info-item:first-child {
  border-top: none;
  padding-top: 4px;
}
.important-info-item:last-child {
  padding-bottom: 12px;
}

/* The (+) / (−) toggle button at the left of each row.
   Replaces the static red bullet — clicking expands or
   collapses the row's body. Sized to match the visual
   weight of the previous bullet but rendered as an
   accessible <button> with hover/focus affordances.
   `flex-shrink: 0` so it never compresses against long
   headline text. */
.important-info-toggle-bullet {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin-top: 0;
  border-radius: 50%;
  background: #c0392b;
  color: #fff;
  border: none;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  /* Optical centering — the +/− glyphs sit a hair high in
     their box, this nudges them down so they look centered. */
  padding: 0 0 1px 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, transform 0.15s ease;
}
.important-info-toggle-bullet:hover,
.important-info-toggle-bullet:focus {
  background: #a02b1f;
  outline: none;
}
/* Subtle scale lift when expanded so the user can see at a
   glance which rows they've already opened. */
.important-info-item.is-expanded .important-info-toggle-bullet {
  background: #2a2a2a;
}

.important-info-body {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #333;
}

/* The bold headline shown in BOTH collapsed and expanded
   states. Always-visible summary of what this message is
   about, drawn from the template string (not from inside
   the ContentBlock) so the Vue template controls the
   wording independent of CMS edits. */
.important-info-headline {
  font-size: 14px;
  font-weight: 700;
  color: #2a2a2a;
  /* Optical alignment with the (+) button — the button is
     22px tall, the headline text is ~21px line-height, so
     a small top nudge keeps them on a shared baseline. */
  padding-top: 1px;
}

/* Italic gray hint shown ONLY in the collapsed state.
   Tells the user how to read more without it feeling like
   a primary call-to-action. */
.important-info-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #777;
  font-style: italic;
}

/* Expanded body — appears when the row is toggled open.
   Slight top spacing so the ContentBlock content doesn't
   crowd against the headline above. */
.important-info-expanded {
  margin-top: 8px;
}

/* Tame ContentBlock's internal markup so each row reads
   consistently regardless of how the CMS structured it.
   Headings inside content blocks should be subtle (no
   competing red headers when the outer container already
   has a red top accent), and paragraph spacing should be
   tight so the rows don't balloon vertically. */
.important-info-body ::v-deep h1,
.important-info-body ::v-deep h2,
.important-info-body ::v-deep h3,
.important-info-body ::v-deep h4 {
  font-size: 13px;
  font-weight: 700;
  color: #2a2a2a;
  margin: 0 0 4px 0;
  text-transform: none;
  letter-spacing: 0;
}
.important-info-body ::v-deep p {
  margin: 0 0 6px 0;
  font-size: 13px;
  line-height: 1.5;
  color: #444;
}
.important-info-body ::v-deep p:last-child {
  margin-bottom: 0;
}
.important-info-body ::v-deep a {
  color: #c0392b;
  text-decoration: none;
  font-weight: 600;
}
.important-info-body ::v-deep a:hover {
  text-decoration: underline;
}
.important-info-body ::v-deep strong,
.important-info-body ::v-deep b {
  font-weight: 700;
  color: #2a2a2a;
}

/* Mobile: tighten side margins so the container doesn't
   crowd the screen edges. */
@media (max-width: 575.98px) {
  .important-info-container,
  .dashboard-welcome-top,
  .dashboard-welcome-subtext-line {
    margin-left: 12px;
    margin-right: 12px;
  }
  .important-info-container {
    padding: 12px 12px 4px;
  }
}

.insights-container {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  padding: 16px 8px 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

/* ===== INSIGHT CARDS — INNER CHROMELESS LAYOUT =====
   The four child blocks (Total Spend banner + 3 stat cards)
   used to each carry their own border + shadow + radius +
   white bg. Now all that visual chrome lives on the outer
   .insights-container; these inner blocks are just centered
   content with internal padding for breathing room.

   Kept text-align center + min-height so the three stat
   cards still align horizontally with consistent heights
   (otherwise Orders w/ a 4-digit number could be taller
   than Saved Carts w/ a 1-digit number, breaking the row). */
.insight-card {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 12px 8px;
  text-align: center;
  box-shadow: none;
  min-height: 110px;
}

.insight-number {
  font-size: 28px;
  font-weight: 700;
  color: #c0392b;
  line-height: 1.2;
}

.insight-link {
  color: #c0392b;
  text-decoration: none;
}

.insight-link:hover {
  text-decoration: underline;
  color: #a93226;
}

.insight-label {
  font-size: 13px;
  font-weight: 600;
  color: #666;
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.insight-subtext {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.4;
}

.insight-mini-link,
.insight-mini-value {
  color: #c0392b;
  font-weight: 700;
  text-decoration: none;
}

.insight-mini-link:hover {
  color: #a93226;
  text-decoration: underline;
}

.insight-subtext-label {
  color: #777;
  font-weight: 500;
  margin-left: 3px;
  margin-right: 3px;
}

.insight-subtext-sep {
  color: #999;
  margin: 0 4px;
}

.top-parts-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
}

.top-parts-header h3 {
  margin: 0;
}

.insights-toggle {
  display: flex;
  gap: 6px;
}

.insights-toggle .btn-outline-dark {
  color: #333;
  border-color: #999;
  background: transparent;
}

.insights-toggle .btn-outline-dark:hover {
  color: #fff;
  background-color: #333;
}

.inner-section-header {
  margin-bottom: 10px;
}

.add-cart-link {
  color: #333;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
}

.add-cart-link:hover {
  text-decoration: underline;
  color: #333;
}

.part-desc {
  display: flex;
  flex-direction: column;
}

.part-desc-main {
  font-weight: 600;
  color: #333;
}

.part-desc-sub {
  font-size: 11px;
  color: #777;
  margin-top: 2px;
}

.cart-icon-svg {
  vertical-align: middle;
  flex-shrink: 0;
}

.part-resolve-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.part-resolve-modal {
  background: #fff;
  padding: 28px 36px;
  border-radius: 8px;
  text-align: center;
  min-width: 280px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
}

.part-resolve-text {
  margin-top: 14px;
  font-weight: 600;
  color: #333;
}

.spinner {
  width: 42px;
  height: 42px;
  margin: 0 auto;
  border: 4px solid #e5e5e5;
  border-top: 4px solid #c0392b;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

.table-notice {
  text-align: right;
  color: #c0392b;
  font-size: 12px;
  margin-top: 10px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 991.98px) {
  .insights-card-row,
  .insights-inner-block,
  #acctInsights .mt-3 {
    margin-left: 0;
    margin-right: 0;
  }

  .account-header-row {
    flex-direction: column;
    align-items: flex-start;
    margin-left: 0;
    margin-right: 0;
  }

  .important-info-toggle {
    margin-left: 0;
  }
}

/* ===== AT-A-GLANCE SUMMARY ROW =====
   Three horizontal cards (Top Parts / Recent Orders / Recent
   Quotes). Replaces the old vertical stack of full-width
   tables so the user sees their account snapshot above the
   fold without scrolling. */
.dashboard-summary-row {
  margin-top: 28px;
  margin-bottom: 16px;
}

/* ===== COMPLETED CARDS ROW (inside col-lg-8) =====
   Sits below the insights container inside the left column.
   Uses Bootstrap's default negative -15px row margins so the
   col-md-6 children's 15px padding is offset and the cards'
   visible edges align EXACTLY with the .insights-container's
   left and right edges above them — without the negative
   margins, the cards were inset 15px on each side and looked
   visibly narrower than the insights container even though
   they're in the same col-lg-8 column.

   Top margin gives breathing room from the insights container
   above; matches the visual rhythm of the insights row's own
   internal spacing so the dashboard reads as evenly spaced
   sections rather than two crammed-together blocks. */
.dashboard-completed-row {
  margin-top: 24px;
  margin-left: -15px;
  margin-right: -15px;
}

/* ===== RIGHT COLUMN FLEX LAYOUT =====
   Make col-lg-4 a vertical flex container so its children
   (staging tray + Top 5 panel) can share the column height
   intelligently:

     - The staging tray takes its natural content height
       (no flex-grow, sits at its required size at the top).
     - Top 5 gets `flex: 1` below so it grows to fill the
       remaining vertical space — and since col-lg-4 itself
       is already stretched by Bootstrap's row default
       (align-items: stretch) to match col-lg-8's height,
       Top 5 ends up matching the combined height of the
       insights container + completed cards row in col-lg-8.

   Result: when staging is hidden, Top 5 fills the entire
   right column flush from the insights container at top
   to the pricing notice at bottom. When staging is visible,
   Top 5 takes whatever space the staging tray didn't claim. */
.right-column-flex {
  display: flex;
  flex-direction: column;
}

/* ===== RIGHT-COLUMN CARD =====
   Top 5 panel: flush with the top of col-lg-4 (no top margin
   so it aligns visually with the insights container's red
   top accent), and `flex: 1` so it stretches vertically to
   fill any unclaimed space in the column. The chained
   selector is kept (specificity 0,2,0) to beat the generic
   .dashboard-card height: 100% rule defined later. */
.dashboard-card.dashboard-card-right {
  height: auto;
  margin-top: 0;
  flex: 1;
}
/* Top 5 now renders via <TopPartsWidget>, whose ROOT element
   (.top-parts-widget) is what sits adjacent to the staging tray —
   the .dashboard-card is nested one level inside it. Target the
   widget root so this spacing keeps working after the extraction
   (a child component's root still carries this parent's scope
   attribute, so the scoped selector reaches it). */
.staging-tray-wrapper + .top-parts-widget {
  /* When a staging tray sits above, restore breathing room
     so the two panels don't visually merge. Otherwise they'd
     touch since the wrapper has no bottom margin. */
  margin-top: 16px;
}

.dashboard-card {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  padding: 14px 16px;
  /* Unified shadow with .insight-card and .staging-tray-wrapper
     so all four card families on the dashboard read as the
     same lifted-panel "tier". Was 0 1px 3px rgba(0,0,0,0.04)
     — too subtle compared to the new insights treatment. */
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.dashboard-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e6e6e6;
}

.dashboard-card-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

/* Slim YTD/All Time toggle for the Top Parts card head. */
.insights-toggle.insights-toggle-sm {
  display: flex;
  gap: 4px;
}

.insights-toggle.insights-toggle-sm .btn.btn-sm {
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

/* List of items inside each card. Each row is a single line
   with main info + action button on the right. */
.dashboard-list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
}

.dashboard-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}

.dashboard-list-item:last-child {
  border-bottom: none;
}

.dashboard-list-main {
  flex: 1 1 auto;
  min-width: 0;
}

/* Top parts: code + description + meta */
.dashboard-list-code {
  font-size: 12px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.2px;
  margin-bottom: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dashboard-list-desc {
  font-size: 11px;
  color: #555;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.dashboard-list-meta {
  font-size: 10px;
  color: #888;
  letter-spacing: 0.2px;
}

.dashboard-list-meta strong {
  color: #444;
  font-weight: 700;
}

/* Recent orders / quotes: date + amount on first line,
   meta below. */
.dashboard-list-row1 {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
}

.dashboard-list-date {
  font-size: 11px;
  font-weight: 600;
  color: #444;
  white-space: nowrap;
}

.dashboard-list-amount {
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  white-space: nowrap;
}

/* Status chip: green for complete, gray for open/in-progress. */
.dashboard-list-status {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.dashboard-list-status.is-complete {
  background: #e8f3e8;
  color: #2d6a2d;
}

.dashboard-list-status.is-open {
  background: #f3f3f3;
  color: #666;
}

/* Add-to-cart icon button (Top Parts card). Slim red icon
   in a circular hover-accent. */
.dashboard-add-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  color: #c0392b;
  text-decoration: none;
  transition: background 0.15s ease;
}

.dashboard-add-btn:hover {
  background: #c0392b;
  color: #fff;
  text-decoration: none;
}

.dashboard-add-btn svg {
  width: 14px;
  height: 14px;
}

/* Edit-state for parts already in the staging tray. Same
   round button shape, but darker icon color (and dark hover)
   to read as "edit existing" rather than "add new" — the
   user gets a visual hint that this row is already in their
   cart and clicking will modify the existing entry, not
   add a duplicate. */
.dashboard-add-btn.is-edit {
  color: #2a2a2a;
}
.dashboard-add-btn.is-edit:hover {
  background: #2a2a2a;
  color: #fff;
}

/* Reorder / Review button on order/quote rows. Small dark
   pill so it doesn't dominate the row. */
.dashboard-action-btn {
  flex-shrink: 0;
  padding: 4px 10px;
  background: #2a2a2a;
  border: none;
  border-radius: 3px;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
}

.dashboard-action-btn:hover {
  background: #c0392b;
}

/* Reorder / Review rendered as round ICON buttons: a baby blue glyph on a
   light-gray circle so each stands out as a tappable control (all modes). */
.dashboard-action-btn.dashboard-action-icon {
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 50%;
  background: #fff;
  color: #7ec8e3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: none;
  letter-spacing: 0;
}
.dashboard-action-btn.dashboard-action-icon svg {
  width: 17px;
  height: 17px;
  fill: currentColor;
}
.dashboard-action-btn.dashboard-action-icon:hover {
  background: #c9ced4;
  color: #5bb0de;
}

/* ===== Per-order company expand (Feature 4) =====
   A small, low-emphasis +/− toggle at the far left of each
   Completed Order row. Subtle gray so it reads as a secondary
   affordance next to the date/amount/status. Rotates the "+"
   into an "×"-like open state via a 45° turn. */
.dashboard-list-expand {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  color: #aaa;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.dashboard-list-expand:hover {
  color: #c0392b;
}
.dashboard-list-expand-icon {
  font-size: 15px;
  font-weight: 700;
  transition: transform 0.15s ease;
}
.dashboard-list-expand.is-open .dashboard-list-expand-icon {
  transform: rotate(45deg);
}

/* The revealed "Company: Name – ID" line. Compact, muted, sits
   directly under the status row inside dashboard-list-main. */
.dashboard-list-company {
  margin-top: 4px;
  font-size: 11px;
  color: #555;
  line-height: 1.3;
}
.dashboard-list-company-label {
  font-weight: 700;
  color: #888;
  margin-right: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 9px;
}
.dashboard-list-company-value {
  font-weight: 600;
  color: #333;
}

/* Empty-state placeholder when no data or no company selected. */
.dashboard-empty {
  padding: 16px 4px;
  font-size: 12px;
  color: #888;
  text-align: center;
  font-style: italic;
}

/* When the empty-state lives inside a stretched right-column
   card (Top 5 with no purchase history, panel sized to match
   col-lg-8's height), center the message vertically inside
   the card body. Without this, the "No purchase history yet."
   text sits glued to the top of the panel just below the
   header, leaving a giant blank space below it. */
.dashboard-card.dashboard-card-right .dashboard-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Tiny print below the row. */
.dashboard-table-notice {
  margin-top: 8px;
  margin-bottom: 16px;
  font-size: 11px;
  color: #888;
  font-style: italic;
}

/* =============================================================
   COMPACT INSIGHT STAT CARDS — used in the upper 8/4-split row.
   The base .insight-card is sized for a full-width row of 4
   cards spanning the container; when nested inside col-lg-8 they
   end up narrower, so we reduce padding + font-size so they
   don't feel cramped.
   ============================================================= */
.insights-card-row-compact {
  margin-bottom: 0;
}
.insight-card-compact {
  padding: 14px 12px;
  min-height: auto;
}
.insight-card-compact .insight-number {
  font-size: 28px;
  line-height: 1.1;
}
.insight-card-compact .insight-label {
  font-size: 11px;
  letter-spacing: 0.04em;
}
.insight-card-compact .insight-subtext {
  font-size: 11px;
}
@media (max-width: 991.98px) {
  /* On tablet+ down, drop the three stat cards to col-md-6
     (2-up wrap) instead of col-4 so they don't crush. The
     full-width banner above them stays full-width regardless. */
  .insights-card-row-compact > .col-4 {
    flex: 0 0 50%;
    max-width: 50%;
    margin-bottom: 12px;
  }
}

/* =============================================================
   STAGING TRAY — right column of the upper grid. Wrapper +
   header give the panel its identity; the inner .insights-* rules
   below match Search.vue's styling for the tray contents so the
   queue rows look identical between the two pages.
   ============================================================= */
.staging-tray-wrapper {
  background: #fff;
  /* Match the .dashboard-card / .insight-card unified
     signature: 1px gray surround + 3px red top. The earlier
     4px red top + no surround stood out from the rest of the
     dashboard; aligning these specifics makes the tray read
     as part of the same panel family. */
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 14px 14px 12px;
  /* Removed height: 100% — when col-lg-4 stretches to match
     col-lg-8 (Bootstrap row flex behavior), height: 100% on
     this wrapper made it fill the entire column, leaving
     Top 5 below it nowhere to go and pushing it visually
     below the dashboard-upper-grid row entirely. Sizing to
     content here lets staging tray + Top 5 stack naturally
     in the right column without fighting for vertical
     space. */
  display: flex;
  flex-direction: column;
}
.staging-tray-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}
.staging-tray-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #c0392b;
}
.staging-tray-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #c0392b;
}
.staging-tray-body {
  flex: 1;
}
.staging-continue-btn {
  width: 100%;
  margin-top: 10px;
  padding: 9px 12px;
  background: #c0392b;
  color: #fff;
  border: 0;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: background 0.15s ease;
}
.staging-continue-btn:hover {
  background: #a3301f;
}
.staging-continue-btn-quote {
  background: #444;
}
.staging-continue-btn-quote:hover {
  background: #222;
}

/* =============================================================
   TOTAL SPEND BANNER — full-width card sitting above the row of
   three smaller stat cards. Wider/flatter than a square stat
   card so the dollar figure has room to breathe and the layout
   reads as "headline number + supporting detail cards."
   ============================================================= */
.insight-card-banner {
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.insight-banner-inner {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  /* Push label + number to the RIGHT edge of the banner so
     they don't visually crowd the YTD / All Time toggle that
     hugs the left side. The toggle is absolutely positioned
     (left: 0) and removed from the flex flow, so the label
     and number get the entire banner row to right-align in. */
  justify-content: flex-end;
  position: relative;
  /* Span the full banner width so absolutely-positioned
     children (the YTD / All Time toggle) place themselves
     against the BANNER's edges, not against this flex item's
     content-sized box. Without this, the inner sizes to just
     the width of its own content (label + number + toggle)
     because its parent .insight-card-banner is itself a flex
     container, so flex-item default sizing applies. The
     toggle's `left: 0` then drops it visually onto the dollar
     figure rather than against the banner's left edge. */
  width: 100%;
  /* Right padding so the dollar figure doesn't touch the
     banner's right edge after the flex-end alignment. */
  padding-right: 16px;
}
.insight-banner-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #555;
}
.insight-banner-number {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
  color: #c0392b;
}

/* YTD / All Time + account-scope toggles hosted INSIDE the Total
   Spend banner. Hugs the LEFT edge via absolute positioning so the
   label/number pair stays optically centered in the banner. The two
   toggle groups (YTD/All Time, then All account/Just mine) lay out
   side by side via flex. On narrow viewports it falls back to flow
   positioning so it never overlaps the dollar figure. */
.insights-toggle-banner {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
/* A subtle divider between the two toggle groups so it's clear they
   are two separate controls sitting next to each other. */
.insights-toggle-scope {
  padding-left: 14px;
  border-left: 1px solid #e0e0e0;
}
/* Keep every toggle button on a single line and a shared height so all
   four (YTD / All Time / All account orders / Just mine) match. Without
   nowrap, "All account orders" wraps to two lines and grows taller than
   the others when it's the active (dark) button. */
.insights-toggle-banner .btn {
  white-space: nowrap;
}
.insights-toggle-banner .insights-toggle {
  align-items: stretch;
}
@media (max-width: 767.98px) {
  .insights-toggle-banner {
    position: static;
    transform: none;
    margin-top: 4px;
  }
}
@media (max-width: 575.98px) {
  .insight-banner-number {
    font-size: 24px;
  }
}

/* When the floating cart is expanded, #app gets .widgets-expanded and the
   dashboard's left content column narrows. The absolutely-positioned toggle
   (left:0) then collides with the right-aligned Total Spend figure and clips
   it (the @media fallbacks above key on VIEWPORT width, which stays wide here
   — only the content is narrowed). Drop the toggle into normal flow and
   center the banner so the figure stays fully visible and reads centered. */
.widgets-expanded .insights-toggle-banner {
  position: static;
  transform: none;
  margin-top: 6px;
  justify-content: center;
}
.widgets-expanded .insight-banner-inner {
  justify-content: center;
  padding-right: 0;
}

/* =============================================================
   STAGING TRAY INNER ROWS — ported verbatim from Search.vue's
   .insights-selected-* / .insights-order-group-* / .insights-queue-*
   rules so the markup-level template can be byte-identical and
   the queue rows look the same on both pages.
   ============================================================= */
.insights-selected-tray {
  margin: 0;
}
.insights-selected-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  margin-bottom: 4px;
}
.insights-selected-title-text {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #444;
}
.insights-selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.insights-selected-label {
  font-size: 11px;
  color: #888;
}
.insights-selected-clear {
  font-size: 11px;
  color: #c0392b;
  background: none;
  border: 0;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
.insights-selected-clear:hover {
  color: #8e2317;
}
.insights-order-group {
  background: #f9f9f9;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 6px;
}
.insights-order-group:first-of-type {
  /* tighter top spacing — header above already provides it */
}
.insights-order-group-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 4px;
}
.insights-order-group-num {
  font-size: 11px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.02em;
}
.insights-order-group-meta {
  font-size: 10px;
  color: #888;
}
.insights-queue-rows {
  list-style: none;
  margin: 0;
  padding: 0;
}
.insights-queue-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #ececec;
  font-size: 12px;
}
.insights-queue-row:last-child {
  border-bottom: 0;
}
.insights-queue-row-code {
  flex: 1;
  font-weight: 600;
  color: #222;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.insights-queue-row-qty {
  display: flex;
  align-items: center;
  gap: 3px;
}
.insights-qty-input {
  width: 50px;
  padding: 2px 4px;
  font-size: 11px;
  border: 1px solid #d6d6d6;
  border-radius: 3px;
  text-align: center;
  background: #fff;
  color: #222;
}
.insights-qty-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.12);
}
.insights-qty-input::-webkit-inner-spin-button,
.insights-qty-input::-webkit-outer-spin-button {
  opacity: 1;
}
.insights-qty-label {
  font-size: 10px;
  color: #888;
}
.insights-queue-row-remove {
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 50%;
  background: #f0f0f0;
  color: #888;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.insights-queue-row-remove:hover {
  background: #c0392b;
  color: #fff;
}

/* Quote section — visually distinct from orders so the user can
   tell the two queues apart at a glance. */
.insights-selected-title-quote {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #e6e6e6;
}
.insights-quote-group .insights-order-group-num {
  color: #555;
}

/* =============================================================
   ADD TO CART MODAL — ported from Search.vue. Uses
   modal-class="add-to-cart-modal" on the b-modal so these rules
   only apply to this modal even though the file is scoped (the
   modal renders into a portal at body, but b-modal applies the
   modal-class to .modal which is inside the portal — leaving
   them unscoped via /deep/ would be the alternate fix; here we
   just write them globally below the scoped block).
   ============================================================= */
</style>

<style>
/* Unscoped because b-modal renders to a body portal — scoped
   styles wouldn't reach it. Class names are namespaced
   (.add-to-cart-*) so collisions are unlikely. */
.add-to-cart-modal .modal-dialog {
  max-width: 480px;
}
/* Re-order modal — keep it compact like the add-to-cart one.
   Unscoped because b-modal renders into a body portal. */
.reorder-modal .modal-dialog {
  max-width: 420px;
}
.add-to-cart-body {
  padding: 0.25rem 0.25rem 0;
}
.add-to-cart-lede {
  font-size: 0.95rem;
  margin-bottom: 1.25rem;
  color: #333;
}
.add-to-cart-partcode {
  color: #c8102e;
  font-weight: 700;
}
.add-to-cart-section-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #555;
  margin-bottom: 0.5rem;
  margin-top: 1rem;
}
.add-to-cart-destination {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}
.add-to-cart-dest-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 0.5rem;
  border: 2px solid #d6d6d6;
  border-radius: 6px;
  background: #fff;
  color: #222;
  cursor: pointer;
  transition: all 0.12s ease-in-out;
  text-align: center;
  min-height: 70px;
}
.add-to-cart-dest-btn:hover {
  border-color: #b3b3b3;
}
.add-to-cart-dest-btn.is-active {
  background: #c8102e;
  border-color: #c8102e;
  color: #fff;
}
.add-to-cart-dest-title {
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.add-to-cart-dest-sub {
  font-size: 0.75rem;
  font-weight: 400;
  margin-top: 0.15rem;
  opacity: 0.85;
}
.add-to-cart-helper {
  font-size: 0.8rem;
  color: #777;
  margin-top: 0.4rem;
  font-style: italic;
}
.add-to-cart-qty-input {
  max-width: 100px;
}
.add-to-cart-remove {
  margin-top: 1.25rem;
  text-align: center;
  font-size: 0.85rem;
}
.add-to-cart-remove a {
  color: #c8102e;
  text-decoration: none;
}
.add-to-cart-remove a:hover {
  text-decoration: underline;
}

/* Modal PO input — Search.vue scopes this under
   .po-explainer-modal but on Account we use it inside the
   add-to-cart-modal, so re-define it generically. */
.add-to-cart-modal .po-modal-input {
  display: block;
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #222;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.add-to-cart-modal .po-modal-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.12);
}

/* PO picker — existing-group buttons inside the modal. */
.add-to-cart-po-picker {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}
.add-to-cart-po-pick-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.7rem 1rem;
  border: 2px solid #d6d6d6;
  border-radius: 6px;
  background: #fff;
  color: #222;
  cursor: pointer;
  transition: all 0.12s ease-in-out;
  text-align: left;
  width: 100%;
}
.add-to-cart-po-pick-btn:hover {
  border-color: #b3b3b3;
  background: #fafafa;
}
.add-to-cart-po-pick-btn.is-active {
  border-color: #c8102e;
  background: #fdf0f2;
}
.add-to-cart-po-pick-btn-num {
  font-weight: 700;
  font-size: 0.95rem;
  color: #222;
}
.add-to-cart-po-pick-btn-meta {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.2rem;
}
.add-to-cart-po-pick-btn.is-active .add-to-cart-po-pick-btn-meta {
  color: #555;
}
.add-to-cart-po-pick-btn-new {
  border-style: dashed;
  align-items: center;
}
.add-to-cart-po-pick-btn-new .add-to-cart-po-pick-btn-num {
  color: #c8102e;
}
.add-to-cart-po-pick-btn-new.is-active {
  border-style: solid;
}

/* ===========================================================
   STAGING-TRAY COLLAPSE BUTTON
   ===========================================================
   The × in the cart panel header. Pushes the icon + title to
   the left while the × parks on the far right via flex
   space-between on .staging-tray-header. Pattern mirrors the
   ×  on the util-toggle pills (Head.vue), so the affordance
   reads identically across the page: an inline × always
   means "collapse this thing to its side-rail pill".

   Note on specificity: the staging-tray-header descendant
   form (`.staging-tray-header .staging-tray-collapse-btn`)
   is used to win over global `button { color: inherit }` /
   text-color cascades from ancestors like
   `.insights-selected-tray` which were rendering the button
   glyph white-on-white in earlier iterations. The chained
   selector raises specificity to (0,2,0) which is the
   minimum needed to overcome the inherited cascade. */
.staging-tray-header {
  /* Ensure the header is a flex row with the × pushed to the
     right. The icon + title cluster sits on the left. */
  justify-content: space-between;
}
.staging-tray-header .staging-tray-collapse-btn,
button.staging-tray-collapse-btn {
  background: transparent;
  border: none;
  color: #c0392b !important;
  font-size: 20px;
  line-height: 1;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
  border-radius: 3px;
  transition: background 0.15s, color 0.15s;
  /* Reset font-family in case a global "button" rule pulled
     in something heavy; the × glyph should render in a
     neutral system font for crisp anti-aliasing. */
  font-family: inherit;
  /* Keep the button visually anchored regardless of any
     ancestor's text-align override. */
  flex: 0 0 auto;
}
.staging-tray-header .staging-tray-collapse-btn:hover,
button.staging-tray-collapse-btn:hover {
  background: #c0392b;
  color: #fff !important;
}

/* ===========================================================
   DASHBOARD-CARD COLLAPSE BUTTON
   ===========================================================
   Twin of the staging-tray collapse button, lives on the Top
   5 panel's .dashboard-card-head row. The head is already
   display:flex with space-between (see #acctInsights
   .dashboard-card-head rule above), so the button naturally
   parks on the right next to (or in place of) any title-row
   toggles. Same specificity-raising pattern as the staging-
   tray collapse button — the inherited text color on
   .dashboard-card was painting the glyph white before. */
/* ===========================================================
   (The .dashboard-card-collapse-btn rules previously here have
   been removed. That button only existed on the inline Top 5
   panel, which is now rendered by TopPartsWidget — the widget
   has its own .top-parts-collapse-btn rules in its scoped
   style block. The staging-tray collapse button above is the
   only inline-× button left on this host.) */

/* ===========================================================
   ACCOUNT SIDE-EDGE PILLS — CART + TOP 5
   ===========================================================
   Two pills tucked into the right viewport edge as tab-like
   pull-handles. Visual language is intentionally identical to
   CartWidget.cart-pill (white card, red left-accent border,
   right-edge tucked silhouette via half-rounded radius, mid-
   right vertical anchor) so the Account page's collapsed cart
   reads as the "same thing" the user sees on
   /account/savedcarts.

   They sit at slightly different vertical anchors so when both
   are collapsed they stack legibly:
     - Cart pill: anchored at top 50%, the "primary" position
       matching CartWidget's default.
     - Top 5 pill: anchored ~90px below the cart pill's center,
       which clears the cart pill's height (~70px tall) plus a
       small visual gap.
   When only one is collapsed it shows at its own anchor
   regardless — the offsets don't depend on the other pill
   being present.

   z-index 9000 mirrors CartWidget's so neither pill loses to
   side rails (999) or sticky header (1000). */
.acct-rail-pill {
  position: fixed;
  right: 12px;
  z-index: 9000;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  background: #fff;
  color: #c0392b;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 8px 0 0 8px;
  /* No right-side border-radius — pill tucks into the right
     viewport edge as a tab. */
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 10px 8px;
  min-width: 44px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, transform 0.18s ease;
}

.acct-rail-pill:hover {
  background: #fafafa;
  transform: translateX(-2px);
}

.acct-rail-pill:focus-visible {
  outline: 2px solid #c0392b;
  outline-offset: 2px;
}

/* Cart pill mid-right, matching CartWidget.cart-pill placement
   so a user navigating between /account and /account/savedcarts
   sees the cart "stay put" visually. */
.acct-rail-pill-cart {
  top: 50%;
  transform: translateY(-50%);
}
.acct-rail-pill-cart:hover {
  transform: translateY(-50%) translateX(-2px);
}

/* (Top 5 pill rules removed — TopPartsWidget renders its own
   .top-parts-pill with self-contained styling, including the
   vertical TOP 5 caption and offset positioning to clear
   the cart pill.) */

/* Icon row inside the pill — same as CartWidget.cart-pill-icon. */
.acct-rail-pill-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Cart count badge cluster + single badge. Mirrors
   CartWidget.cart-pill-counts/cart-pill-badge styling but
   uses one combined badge (orders + quotes summed) since
   Account's staging tray combines both queues. */
.acct-rail-pill-counts {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
}
.acct-rail-pill-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  background: #c0392b;
}

/* ===========================================================
   COLUMN-WIDTH ALIGNMENT (Cart col ↔ Top 5 col)
   ===========================================================
   Cart column lives inside #acctImportantInfo's default
   Bootstrap .container (15px gutter). Top 5 column lives
   inside #acctInsights's .container which we already
   normalized to 15px above (see steele-consistency-fix.css).
   But measured widths still differ: cart 358px vs Top 5
   338px, with Top 5 starting 10px further left. The
   remaining mismatch comes from the .dashboard-upper-grid
   row carrying inherited margin/padding that doesn't match
   .acct-important-row.

   Two fixes layered together:
     1. Zero out .dashboard-upper-grid's horizontal margins so
        its inner columns measure from the same x-coordinate
        as .acct-important-row's columns.
     2. Pin both target col-lg-4s to identical width via
        flex-basis + max-width as a belt-and-suspenders
        guarantee — Bootstrap's defaults set these to
        33.333333% already, but stating them explicitly with
        higher specificity prevents any future utility class
        from drifting one column off the other. */
.dashboard-upper-grid {
  margin-left: 0;
  margin-right: 0;
}
.dashboard-upper-grid > [class*="col-"] {
  /* Match .acct-important-row's columns, which carry the
     default Bootstrap col gutter (15px L/R padding). Setting
     it here explicitly removes any inherited padding override
     from elsewhere in the cascade. */
  padding-left: 15px;
  padding-right: 15px;
}
@media (min-width: 992px) {
  .acct-important-row > .acct-important-cart-col,
  .dashboard-upper-grid > .right-column-flex {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }
}

/* ===========================================================
   CLOSE THE GAP between #acctImportantInfo and #acctInsights
   ===========================================================
   When the cart is showing in acct-important-cart-col, the
   #acctImportantInfo section's row is TALLER than just the
   Important Information card (the cart is taller). This
   leaves visible whitespace below the Important Info card
   on the left, before the next section (#acctInsights)
   starts. The user wants #acctInsights pulled UP so it
   visually starts right below Important Info, rather than
   sitting in a "no man's land" below the cart.

   Two-part fix:

   1. Take the cart column OUT of the row's height
      calculation by giving it `align-self: flex-start`. With
      the default `align-items: stretch` on the row, the cart
      stretches to match Important Info's height (which it
      doesn't actually fill), and worse, the whole row's
      `height` is set to whatever's TALLEST — so even when
      the cart is taller than Important Info, the row's
      bounding-box height becomes the cart's height. Switching
      the cart to flex-start sizes the row to its NATURAL
      (Important Info) height, with the cart overflowing
      downward visually — but only on the right third of the
      page where there's no other content to collide with
      (col-lg-4, right edge).

   2. Pull #acctInsights up by a fixed -16px so the section
      hugs Important Info's bottom edge. We use a small,
      bounded value here (NOT a huge negative margin) because
      the cart now visually extends down into the same
      horizontal lane as the insights stats below — letting
      acctInsights start naturally below ImportantInfo means
      the cart's tail end now sits to the RIGHT of the
      insights stats (col-lg-4 lane), which is exactly the
      "cart sits next to acctInsights" effect the user
      wanted.

   Hidden on screens narrower than 992px (Bootstrap's lg
   breakpoint) because below that the cart stacks vertically
   instead of sitting alongside Important Info, so neither
   fix applies. */
@media (min-width: 992px) {
  #account.has-inline-cart .acct-important-row {
    /* Default is stretch — switch to flex-start so the row's
       height is governed by the SHORTER column (Important
       Info), not the taller column (Cart). */
    align-items: flex-start;
  }
  #account.has-inline-cart .acct-important-row > .acct-important-cart-col {
    /* Belt to the row-level align-items above. Some Bootstrap
       installs add `.row > [class*="col-"]` rules that
       override align-items at the child level, so we re-state
       it here on the column for safety. */
    align-self: flex-start;
  }
  #account.has-inline-cart #acctInsights {
    /* Tight hug to ImportantInfo's bottom. The cart's tail
       now overflows naturally into this section's right-
       column horizontal lane. */
    margin-top: -16px;
  }
}

/* When the user has parked Top 5 (TopPartsWidget emits
   inline-visibility-change(false), mirrored locally as
   topPartsInlineVisible = false) AND there's no inline cart
   either, the insights row is just the stat cards stretched
   to col-12. The completed-cards row below is also col-12 on
   the same row — nothing else changes about layout. The host
   class on #account (.has-inline-top-parts) is added for
   symmetry with .has-inline-cart so external stylesheets can
   hook into it the same way. */

/* ===== Re-order mismatch prompt (Feature 1) =====
   Unscoped because b-modal renders to a body portal — scoped
   styles wouldn't reach it. */
.reorder-mismatch-lead {
  font-size: 14px;
  font-weight: 700;
  color: #2a2a2a;
  margin-bottom: 10px;
}
.reorder-mismatch-facts {
  list-style: none;
  padding: 0;
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #444;
}
.reorder-mismatch-facts li {
  padding: 3px 0;
}
.reorder-mismatch-facts strong {
  color: #c0392b;
}
.reorder-mismatch-help {
  font-size: 13px;
  font-weight: 600;
  color: #2a2a2a;
  margin-bottom: 10px;
}
.reorder-mismatch-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}
.reorder-mismatch-btn {
  width: 100%;
  font-weight: 600;
}
.reorder-mismatch-caveat {
  font-size: 11px;
  color: #888;
  line-height: 1.4;
  margin: 0;
}

/* ===== Re-order "Add to my company" FAILURE panel ===== */
.reorder-fail-title {
  font-size: 14px;
  font-weight: 700;
  color: #c0392b;
  margin-bottom: 10px;
}
.reorder-fail-reason {
  font-size: 13px;
  color: #333;
  background: #fdf3f2;
  border: 1px solid #f3c9c4;
  border-radius: 6px;
  padding: 8px 10px;
  line-height: 1.4;
  margin-bottom: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}
.reorder-fail-diagnosing {
  font-size: 12px;
  color: #888;
  font-style: italic;
  margin-bottom: 12px;
}
.reorder-fail-subhead {
  font-size: 12px;
  font-weight: 700;
  color: #2a2a2a;
  margin-bottom: 6px;
}
.reorder-fail-list {
  list-style: none;
  padding: 0;
  margin: 0 0 12px 0;
}
.reorder-fail-list li {
  padding: 6px 8px;
  border: 1px solid #eee;
  border-left: 3px solid #c0392b;
  border-radius: 4px;
  margin-bottom: 6px;
  font-size: 12px;
}
.reorder-fail-kind {
  display: inline-block;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.03em;
  color: #c0392b;
  margin-right: 6px;
}
.reorder-fail-name {
  font-weight: 600;
  color: #2a2a2a;
}
.reorder-fail-fix {
  display: block;
  color: #555;
  margin-top: 2px;
}
.reorder-fail-actions,
.reorder-fail-retry,
.reorder-fail-alt {
  margin-bottom: 10px;
}
/* Two stacked add choices: give the buttons a little breathing room. */
.reorder-fail-actions .reorder-mismatch-btn + .reorder-mismatch-btn {
  margin-top: 6px;
}
.reorder-fail-temp-note {
  font-size: 12px;
  color: #555;
  margin-top: 6px;
  margin-bottom: 0;
}
.reorder-fail-retry-note {
  font-size: 12px;
  font-weight: 600;
  color: #1e7e34;
  margin-bottom: 6px;
}
</style>